/**
 * Assembles final HTML from esbuild output + index.html template.
 * Injects import map, bundled CSS, and bundled JS into the HTML.
 */
import { PREVIEW_NAV_GUARD_SCRIPT } from './previewGuardScript';
export { PREVIEW_NAV_GUARD_SCRIPT };
import { PREVIEW_INSPECTOR_SCRIPT } from './previewInspectorScript';
export { PREVIEW_INSPECTOR_SCRIPT };

// Module bootstrap script as embedded string — works in both Vite and Wrangler.
// Generated from preview-module-bootstrap.js by scripts/prepare-dist.mjs.
import { moduleBootstrapRaw } from './previewModuleBootstrapRaw';

const PREVIEW_VERBOSE_LOGS =
  typeof globalThis !== 'undefined' &&
  Boolean((globalThis as any).__TASKING_PREVIEW_DEBUG__);

function previewVerboseLog(...args: unknown[]) {
  if (!PREVIEW_VERBOSE_LOGS) return;
  console.log(...args);
}

export interface AssembleOptions {
  indexHtml: string;
  bundledJs: string;
  bundledCss: string;
  importMap: Record<string, string>;
  hasTailwind: boolean;
  hasAngular?: boolean;
  framework?: 'react' | 'vue' | 'svelte' | 'angular' | 'vanilla' | string;
}

// ── Font + CSS baseline injected into EVERY preview (Tailwind or not) ──
// Provides Google Fonts, a CSS reset, and sensible defaults so AI-generated sites
// look polished regardless of whether Tailwind is used.
export const FONT_BASELINE = `
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: optimizeLegibility; }
  body { font-family: 'Inter', 'DM Sans', system-ui, -apple-system, sans-serif; line-height: 1.6; }
  h1, h2, h3, h4, h5, h6 { font-family: 'Space Grotesk', 'Plus Jakarta Sans', system-ui, sans-serif; line-height: 1.2; }
  code, pre, kbd { font-family: 'JetBrains Mono', 'Fira Code', ui-monospace, monospace; }
  img, video, svg { max-width: 100%; height: auto; }
</style>
`;

// ── Design baseline injected into every Tailwind-enabled preview ──
// Extends FONT_BASELINE with:
// 1. Plain <style> with shadcn :root CSS variables (ALWAYS applied — no Tailwind needed)
// 2. <style type="text/tailwindcss"> with @theme tokens + @theme inline color mappings
//    (processed by @tailwindcss/browser@4 to generate utility classes)
// Split into two parts so that even if the Tailwind browser script fails to load,
// the CSS custom property values are always defined and dark mode always works.
export const TAILWIND_DESIGN_BASELINE = `${FONT_BASELINE}<script>/* Dark mode by default — runs synchronously to prevent flash of light theme */
document.documentElement.classList.add('dark');
</script>
<style>
/* Design tokens — Dark theme by default (matches tasking.tech color system) */
:root {
  --background: hsl(220 15% 8%);
  --foreground: hsl(220 10% 97%);
  --surface: hsl(220 15% 11%);
  --surface-foreground: hsl(220 10% 95%);
  --sidebar: hsl(220 15% 10%);
  --sidebar-foreground: hsl(220 10% 80%);
  --card: hsl(220 15% 13%);
  --card-foreground: hsl(220 10% 95%);
  --popup: hsl(220 15% 15%);
  --popup-foreground: hsl(220 10% 98%);
  --primary: hsl(25 90% 55%);
  --primary-foreground: hsl(25 10% 10%);
  --secondary: hsl(220 10% 25%);
  --secondary-foreground: hsl(220 10% 90%);
  --accent: hsl(145 70% 45%);
  --accent-foreground: hsl(145 10% 5%);
  --muted: hsl(220 10% 18%);
  --muted-foreground: hsl(220 10% 65%);
  --destructive: hsl(0 80% 60%);
  --destructive-foreground: hsl(0 10% 98%);
  --border: hsl(220 10% 22%);
  --input: hsl(220 10% 22%);
  --ring: hsl(25 90% 55%);
  --error: hsl(0 80% 60%);
  --error-foreground: hsl(0 10% 98%);
  --warning: hsl(45 90% 50%);
  --warning-foreground: hsl(45 10% 10%);
  --success: hsl(145 70% 45%);
  --success-foreground: hsl(145 10% 5%);
  --info: hsl(200 80% 50%);
  --info-foreground: hsl(200 10% 95%);
  --radius: 12px;
  --radius-sm: 8px;
  --radius-md: 14px;
  --radius-lg: 16px;
  --radius-xl: 20px;
  --shadow: 0 1px 3px 0 rgb(0 0 0/0.3),0 1px 2px -1px rgb(0 0 0/0.2);
  --shadow-md: 0 4px 8px -2px rgb(0 0 0/0.4),0 2px 4px -2px rgb(0 0 0/0.3);
  --shadow-lg: 0 12px 24px -4px rgb(0 0 0/0.5),0 4px 8px -4px rgb(0 0 0/0.4);
}
/* Dark class — explicit dark mode (same values as :root default) */
.dark {
  --background: hsl(220 15% 8%);
  --foreground: hsl(220 10% 97%);
  --surface: hsl(220 15% 11%);
  --surface-foreground: hsl(220 10% 95%);
  --sidebar: hsl(220 15% 10%);
  --sidebar-foreground: hsl(220 10% 80%);
  --card: hsl(220 15% 13%);
  --card-foreground: hsl(220 10% 95%);
  --popup: hsl(220 15% 15%);
  --popup-foreground: hsl(220 10% 98%);
  --primary: hsl(25 90% 55%);
  --primary-foreground: hsl(25 10% 10%);
  --secondary: hsl(220 10% 25%);
  --secondary-foreground: hsl(220 10% 90%);
  --accent: hsl(145 70% 45%);
  --accent-foreground: hsl(145 10% 5%);
  --muted: hsl(220 10% 18%);
  --muted-foreground: hsl(220 10% 65%);
  --destructive: hsl(0 80% 60%);
  --destructive-foreground: hsl(0 10% 98%);
  --border: hsl(220 10% 22%);
  --input: hsl(220 10% 22%);
  --ring: hsl(25 90% 55%);
  --error: hsl(0 80% 60%);
  --error-foreground: hsl(0 10% 98%);
  --warning: hsl(45 90% 50%);
  --warning-foreground: hsl(45 10% 10%);
  --success: hsl(145 70% 45%);
  --success-foreground: hsl(145 10% 5%);
  --info: hsl(200 80% 50%);
  --info-foreground: hsl(200 10% 95%);
}
/* Light theme override */
.light {
  --background: hsl(220 20% 97%);
  --foreground: hsl(220 15% 15%);
  --surface: hsl(220 15% 94%);
  --surface-foreground: hsl(220 15% 10%);
  --sidebar: hsl(220 15% 96%);
  --sidebar-foreground: hsl(220 10% 25%);
  --card: hsl(0 0% 100%);
  --card-foreground: hsl(220 15% 10%);
  --popup: hsl(0 0% 100%);
  --popup-foreground: hsl(220 15% 10%);
  --primary: hsl(25 95% 53%);
  --primary-foreground: hsl(0 0% 100%);
  --secondary: hsl(220 10% 85%);
  --secondary-foreground: hsl(220 15% 20%);
  --accent: hsl(145 70% 40%);
  --accent-foreground: hsl(0 0% 100%);
  --muted: hsl(220 10% 90%);
  --muted-foreground: hsl(220 15% 35%);
  --destructive: hsl(0 80% 55%);
  --destructive-foreground: hsl(0 0% 100%);
  --border: hsl(220 10% 85%);
  --input: hsl(220 10% 85%);
  --ring: hsl(25 95% 53%);
  --error: hsl(0 80% 55%);
  --error-foreground: hsl(0 0% 100%);
  --warning: hsl(38 92% 50%);
  --warning-foreground: hsl(220 15% 10%);
  --success: hsl(145 70% 40%);
  --success-foreground: hsl(0 0% 100%);
  --info: hsl(200 80% 45%);
  --info-foreground: hsl(0 0% 100%);
  --shadow: 0 1px 2px 0 rgb(0 0 0/0.1);
  --shadow-md: 0 4px 6px -1px rgb(0 0 0/0.1),0 2px 4px -2px rgb(0 0 0/0.1);
  --shadow-lg: 0 10px 15px -3px rgb(0 0 0/0.15),0 4px 6px -4px rgb(0 0 0/0.1);
}
@keyframes accordion-down { from { height: 0; } to { height: var(--radix-accordion-content-height); } }
@keyframes accordion-up { from { height: var(--radix-accordion-content-height); } to { height: 0; } }
/* Immediate background fallback — prevents black flash before @tailwindcss/browser@4 CDN loads.
   These are plain CSS properties (no @apply) so they work without the Tailwind browser script. */
html { color-scheme: dark; }
html, body, #root, #app { min-height: 100vh; }
body {
  background-color: var(--background);
  color: var(--foreground);
  font-family: 'Inter', 'DM Sans', system-ui, -apple-system, sans-serif;
  margin: 0;
  line-height: 1.5;
}
/* ── Base utility fallback ──
   If @tailwindcss/browser@4 fails to load or compile, these plain CSS rules
   provide the most critical Tailwind-like utilities so the UI is still usable.
   When the browser script succeeds, its layered output takes precedence. */
.flex { display: flex; }
.inline-flex { display: inline-flex; }
.grid { display: grid; }
.hidden { display: none; }
.block { display: block; }
.inline { display: inline; }
.relative { position: relative; }
.absolute { position: absolute; }
.fixed { position: fixed; }
.sticky { position: sticky; }
.items-center { align-items: center; }
.items-start { align-items: flex-start; }
.items-end { align-items: flex-end; }
.justify-center { justify-content: center; }
.justify-between { justify-content: space-between; }
.justify-start { justify-content: flex-start; }
.justify-end { justify-content: flex-end; }
.flex-col { flex-direction: column; }
.flex-row { flex-direction: row; }
.flex-wrap { flex-wrap: wrap; }
.flex-1 { flex: 1 1 0%; }
.flex-shrink-0, .shrink-0 { flex-shrink: 0; }
.gap-1 { gap: 0.25rem; }
.gap-2 { gap: 0.5rem; }
.gap-3 { gap: 0.75rem; }
.gap-4 { gap: 1rem; }
.gap-6 { gap: 1.5rem; }
.gap-8 { gap: 2rem; }
.p-0 { padding: 0; }
.p-1 { padding: 0.25rem; }
.p-2 { padding: 0.5rem; }
.p-3 { padding: 0.75rem; }
.p-4 { padding: 1rem; }
.p-6 { padding: 1.5rem; }
.p-8 { padding: 2rem; }
.px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
.px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
.px-4 { padding-left: 1rem; padding-right: 1rem; }
.px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
.py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
.py-4 { padding-top: 1rem; padding-bottom: 1rem; }
.m-0 { margin: 0; }
.m-auto { margin: auto; }
.mx-auto { margin-left: auto; margin-right: auto; }
.mt-1 { margin-top: 0.25rem; }
.mt-2 { margin-top: 0.5rem; }
.mt-4 { margin-top: 1rem; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-4 { margin-bottom: 1rem; }
.ml-auto { margin-left: auto; }
.mr-2 { margin-right: 0.5rem; }
.w-full { width: 100%; }
.w-auto { width: auto; }
.h-full { height: 100%; }
.h-auto { height: auto; }
.h-screen { height: 100vh; }
.min-h-screen { min-height: 100vh; }
.max-w-full { max-width: 100%; }
.overflow-hidden { overflow: hidden; }
.overflow-auto { overflow: auto; }
.overflow-y-auto { overflow-y: auto; }
.rounded { border-radius: var(--radius, 12px); }
.rounded-sm { border-radius: var(--radius-sm, 8px); }
.rounded-md { border-radius: var(--radius-md, 14px); }
.rounded-lg { border-radius: var(--radius-lg, 16px); }
.rounded-xl { border-radius: var(--radius-xl, 20px); }
.rounded-full { border-radius: 9999px; }
.rounded-none { border-radius: 0; }
.border { border: 1px solid var(--border, hsl(220 10% 22%)); }
.border-0, .border-none { border: none; }
.border-b { border-bottom: 1px solid var(--border, hsl(220 10% 22%)); }
.border-t { border-top: 1px solid var(--border, hsl(220 10% 22%)); }
.border-border { border-color: var(--border, hsl(220 10% 22%)); }
.shadow { box-shadow: var(--shadow, 0 1px 3px 0 rgb(0 0 0/0.3)); }
.shadow-sm { box-shadow: 0 1px 2px 0 rgb(0 0 0/0.25); }
.shadow-md { box-shadow: var(--shadow-md, 0 4px 8px -2px rgb(0 0 0/0.4)); }
.shadow-lg { box-shadow: var(--shadow-lg, 0 12px 24px -4px rgb(0 0 0/0.5)); }
.shadow-none { box-shadow: none; }
.text-xs { font-size: 0.75rem; line-height: 1rem; }
.text-sm { font-size: 0.875rem; line-height: 1.25rem; }
.text-base { font-size: 1rem; line-height: 1.5rem; }
.text-lg { font-size: 1.125rem; line-height: 1.75rem; }
.text-xl { font-size: 1.25rem; line-height: 1.75rem; }
.text-2xl { font-size: 1.5rem; line-height: 2rem; }
.text-3xl { font-size: 1.875rem; line-height: 2.25rem; }
.font-normal { font-weight: 400; }
.font-medium { font-weight: 500; }
.font-semibold { font-weight: 600; }
.font-bold { font-weight: 700; }
.text-center { text-align: center; }
.text-left { text-align: left; }
.text-right { text-align: right; }
.whitespace-nowrap { white-space: nowrap; }
.truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.leading-none { line-height: 1; }
.leading-tight { line-height: 1.25; }
.tracking-tight { letter-spacing: -0.025em; }
.cursor-pointer { cursor: pointer; }
.pointer-events-none { pointer-events: none; }
.select-none { user-select: none; }
.opacity-0 { opacity: 0; }
.opacity-50 { opacity: 0.5; }
.opacity-100 { opacity: 1; }
.transition { transition-property: color, background-color, border-color, box-shadow, opacity; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
.transition-colors { transition-property: color, background-color, border-color; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
.transition-all { transition-property: all; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
.duration-200 { transition-duration: 200ms; }
.duration-300 { transition-duration: 300ms; }
.z-10 { z-index: 10; }
.z-50 { z-index: 50; }
.inset-0 { inset: 0; }
.top-0 { top: 0; }
.left-0 { left: 0; }
.right-0 { right: 0; }
.bottom-0 { bottom: 0; }
/* Semantic color utilities — map to CSS custom properties */
.bg-background { background-color: var(--background); }
.bg-foreground { background-color: var(--foreground); }
.bg-card { background-color: var(--card); }
.bg-popover { background-color: var(--popup); }
.bg-primary { background-color: var(--primary); }
.bg-secondary { background-color: var(--secondary); }
.bg-muted { background-color: var(--muted); }
.bg-accent { background-color: var(--accent); }
.bg-destructive { background-color: var(--error); }
.bg-transparent { background-color: transparent; }
.text-foreground { color: var(--foreground); }
.text-card-foreground { color: var(--card-foreground); }
.text-popover-foreground { color: var(--popup-foreground); }
.text-primary { color: var(--primary); }
.text-primary-foreground { color: var(--primary-foreground); }
.text-secondary-foreground { color: var(--secondary-foreground); }
.text-muted-foreground { color: var(--muted-foreground); }
.text-accent-foreground { color: var(--accent-foreground); }
.text-destructive { color: var(--error); }
.text-destructive-foreground { color: var(--error-foreground); }
.text-white { color: #fff; }
.text-black { color: #000; }
.ring-ring { --tw-ring-color: var(--ring, var(--primary)); }
.border-input { border-color: var(--input, var(--border)); }
.outline-none { outline: none; }
/* Focus-visible ring for accessibility */
.focus-visible\\:outline-none:focus-visible { outline: none; }
.focus-visible\\:ring-1:focus-visible { box-shadow: 0 0 0 1px var(--ring, var(--primary)); }
.focus-visible\\:ring-2:focus-visible { box-shadow: 0 0 0 2px var(--ring, var(--primary)); }
.focus-visible\\:ring-ring:focus-visible { box-shadow: 0 0 0 2px var(--ring, var(--primary)); }
/* Disabled state */
.disabled\\:pointer-events-none:disabled { pointer-events: none; }
.disabled\\:opacity-50:disabled { opacity: 0.5; }
/* Hover utilities */
.hover\\:bg-accent:hover { background-color: var(--accent); }
.hover\\:bg-muted:hover { background-color: var(--muted); }
.hover\\:bg-primary\\/90:hover { background-color: color-mix(in srgb, var(--primary) 90%, transparent); }
.hover\\:bg-secondary\\/80:hover { background-color: color-mix(in srgb, var(--secondary) 80%, transparent); }
.hover\\:text-accent-foreground:hover { color: var(--accent-foreground); }
.hover\\:opacity-100:hover { opacity: 1; }
/* Space utilities */
.space-y-1 > * + * { margin-top: 0.25rem; }
.space-y-2 > * + * { margin-top: 0.5rem; }
.space-y-4 > * + * { margin-top: 1rem; }
.space-x-2 > * + * { margin-left: 0.5rem; }
.space-x-4 > * + * { margin-left: 1rem; }
/* Grid columns */
.grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
.grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.col-span-full { grid-column: 1 / -1; }
/* Size utilities */
.size-4 { width: 1rem; height: 1rem; }
.size-5 { width: 1.25rem; height: 1.25rem; }
.size-6 { width: 1.5rem; height: 1.5rem; }
/* Screen reader only */
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border-width: 0; }
/* Dark mode variant fallback — .dark class on <html> or parent */
.dark .dark\\:bg-background { background-color: var(--background); }
.dark .dark\\:text-foreground { color: var(--foreground); }
.dark .dark\\:border-border { border-color: var(--border); }
</style>
<style type="text/tailwindcss">
@theme {
  --font-sans: 'Inter', 'DM Sans', 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
  --font-heading: 'Space Grotesk', 'Plus Jakarta Sans', system-ui, sans-serif;
  --font-mono: 'JetBrains Mono', 'Fira Code', ui-monospace, monospace;
  /* Border radius tiers */
  --radius-xl: 20px;
  --radius-lg: 16px;
  --radius-md: 14px;
  --radius-sm: 8px;
  /* Container sizes */
  --container-sm: 640px;
  --container-md: 768px;
  --container-lg: 1024px;
  --container-xl: 1280px;
  --container-2xl: 1536px;
  /* Animations */
  --animate-accordion-down: accordion-down 0.2s ease-out;
  --animate-accordion-up: accordion-up 0.2s ease-out;
}
@theme inline {
  /* Core color tokens → Tailwind utility classes */
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popup);
  --color-popover-foreground: var(--popup-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-destructive-foreground: var(--destructive-foreground);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);
  /* Extended semantic tokens */
  --color-surface: var(--surface);
  --color-surface-foreground: var(--surface-foreground);
  --color-sidebar: var(--sidebar);
  --color-sidebar-foreground: var(--sidebar-foreground);
  --color-error: var(--error);
  --color-error-foreground: var(--error-foreground);
  --color-warning: var(--warning);
  --color-warning-foreground: var(--warning-foreground);
  --color-success: var(--success);
  --color-success-foreground: var(--success-foreground);
  --color-info: var(--info);
  --color-info-foreground: var(--info-foreground);
}
</style>
<script src="https://unpkg.com/lucide@latest"></script>
<script>document.addEventListener('DOMContentLoaded',function(){if(window.lucide)window.lucide.createIcons()});</script>
`;

// Names that often exist elsewhere in the bundle; use a unique alias to avoid "already been declared".
const LUCIDE_CONFLICT_NAMES = new Set<string>(['size', 'color', 'ref', 'key', 'variant']);

// Track which specifiers have shim URLs - populated by normalize functions
// and consumed by assembleHtml for URL inlining. Must be declared at module
// level since normalizeSvelteRuntime adds to it before assembleHtml runs.
const shimmedSpecifiers: string[] = [];

function rewriteLucideNamedImports(code: string): string {
  let rewriteCounter = 0;
  const conflictEmitted = new Set<string>(); // track so we only declare once per name
  let rewritten = code.replace(
    /import\s+(?:([$A-Z_a-z][$\w]*)\s*,\s*)?\{([\s\S]*?)\}\s*from\s*["']lucide-react["']\s*;?/g,
    (_full, defaultImport: string | undefined, namedBlock: string) => {
      const nsVar = `__taskingLucide${rewriteCounter++}`;
      // Use globalThis.__TASKING_Lucide (pre-loaded by the wrapper script which CAN
      // use the import map) instead of a static import. Blob URL modules can't reliably
      // resolve bare specifiers or CDN modules that themselves import "react" as a bare
      // specifier. The globalThis pattern is proven to work (same as React/ReactDOM).
      const lines: string[] = [`var ${nsVar} = globalThis.__TASKING_Lucide || {};`];

      if (defaultImport) {
        const defaultIsConflict = LUCIDE_CONFLICT_NAMES.has(defaultImport);
        const alreadyEmitted = defaultIsConflict && conflictEmitted.has(defaultImport);
        if (!alreadyEmitted) {
          if (defaultIsConflict) conflictEmitted.add(defaultImport);
          const defaultAlias = defaultIsConflict
            ? `__taskingLucide_${defaultImport}`
            : defaultImport;
          lines.push(`const ${defaultAlias} = ${nsVar}.default ?? ${nsVar};`);
        }
      }

      const specs = namedBlock
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);

      for (const spec of specs) {
        const match = spec.match(/^([$A-Z_a-z][$\w]*)(?:\s+as\s+([$A-Z_a-z][$\w]*))?$/);
        if (!match) continue;
        const original = match[1];
        const local = match[2] || original;
        const isConflict = LUCIDE_CONFLICT_NAMES.has(local);
        const alias = isConflict ? `__taskingLucide_${local}` : local;
        if (isConflict && conflictEmitted.has(local)) {
          continue;
        }
        if (isConflict) conflictEmitted.add(local);
        // Keep these rewrites pure and avoid touching declaration keywords elsewhere.
        // Also set on globalThis so it's accessible from ALL blob URL module scopes.
        lines.push(
          `const ${alias} = globalThis["${original}"] = ${nsVar}.${original} ?? ${nsVar}.ArrowUpDown ?? ${nsVar}.ChevronsUpDown ?? ${nsVar}.ArrowUpAZ ?? ${nsVar}.ArrowUpWideNarrow ?? ${nsVar}.ListFilter ?? ${nsVar}.Circle ?? (() => null);`,
        );
      }

      return lines.join('\n');
    },
  );

  // Replace non-declaration references to conflict names so they use our alias.
  // Protect actual declarations (const/let/var) by temporarily replacing them with a placeholder.
  const conflictList = Array.from(conflictEmitted);
  conflictList.forEach((name, i) => {
    const alias = `__taskingLucide_${name}`;
    const keepPlaceholder = `__taskingLucide_DECL_${i}`;

    // Protect declaration sites so we don't accidentally change `const size = ...` -> `const __taskingLucide_size`
    rewritten = rewritten.replace(
      new RegExp(`(\\bconst|\\blet|\\bvar)\\s+${escapeRegExp(name)}\\b`, 'g'),
      `$1 ${keepPlaceholder}`,
    );

    // Replace other occurrences of the plain name with alias
    rewritten = rewritten.replace(new RegExp(`\\b${escapeRegExp(name)}\\b`, 'g'), alias);

    // Restore placeholders back to original declaration name
    rewritten = rewritten.replace(new RegExp(escapeRegExp(keepPlaceholder), 'g'), name);
  });

  return rewritten;
}

// Run this once, on the user bundle, before assembleHtml/Blob creation.
export function sanitizeNullObjectOps(code: string): string {
  // DISABLED: The Object.assign regex corrupts code with nested parentheses.
  // e.g. Object.assign(a, fn(g(h), k)) → broken output with mismatched parens.
  // The spread regex is safe but provides minimal value.
  // Runtime null-safety should be handled at the component level, not by
  // blindly rewriting bundled output.
  return code;
}

export function sanitizeDuplicates(code: string): string {
  let out = code;

  // 1) Normalize top-level function/class/const/let to var on second+ occurrences.
  // Include } and ) as valid prefix characters — esbuild output may place declarations
  // directly after closing braces/parens without a newline or semicolon.
  // Strategy: keep first declaration, convert duplicates to `var` (allows redeclaration).
  const declRegex =
    /(^|[;\n}\)])\s*(?:export\s+)?(async\s+)?(function|class|const|let|var)\s+([A-Za-z_$][\w$]*)/g;
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  out = out.replace(declRegex, (full, prefix, asyncKw = '', kind, name) => {
    if (!seen.has(name)) {
      seen.add(name);
      return full;
    }

    duplicates.add(name);

    // Convert to var for redeclaration safety (var allows re-declaration in same scope)
    if (kind === 'function') {
      return `${prefix}var ${name} = ${asyncKw || ''}function ${name}`;
    }
    if (kind === 'class') {
      return `${prefix}var ${name} = class ${name}`;
    }
    // const/let → var (allows redeclaration without SyntaxError)
    return full.replace(/(const|let)\s+/, 'var ');
  });

  // Debug-only diagnostics — only log in development, and make the message actionable.
  try {
    if (duplicates.size > 0) {
      const names = Array.from(duplicates).slice(0, 20);
      console.debug(
        `[sanitizeDuplicates] resolved ${duplicates.size} duplicate declaration(s): ${names.join(', ')}` +
          (duplicates.size > 20 ? ` (+${duplicates.size - 20} more)` : ''),
      );
    }
  } catch (_e) {}

  // 2) Fix multi-variable declarations where the same name appears in a comma list.
  // e.g. `const a = 1, a = 2, b = 3;` → keep first occurrence, drop later ones.
  const multiDeclRegex = /(^|\n)\s*(const|let|var)\s+([^;]+);/g;
  out = out.replace(multiDeclRegex, (full, prefix, kind, body) => {
    const parts = body.split(',');
    const localSeen = new Set<string>();
    const decls: string[] = [];

    for (const raw of parts) {
      const part = raw.trim();
      if (!part) continue;
      const m = part.match(/^([A-Za-z_$][\w$]*)(\s*=\s*[\s\S]+)?$/);
      if (!m) {
        decls.push(part);
        continue;
      }
      const name = m[1];
      const init = m[2] || '';
      if (!localSeen.has(name)) {
        localSeen.add(name);
        decls.push(`${name}${init}`);
      }
      // Drop later duplicates silently — this is usually AI generating the same
      // component/variable name twice in a single declaration.
    }

    if (decls.length === 0) return '';
    return `${prefix}${kind} ${decls.join(', ')};`;
  });

  return out;
}

// Aggressive sanitizer: runs the regular sanitizer followed by an extra pass
// that matches declarations after semicolons/newlines (handles minified bundles).
export function sanitizeDuplicatesAggressive(code: string): string {
  let out = sanitizeDuplicates(code);
  try {
    const seen = new Set<string>();
    out = out.replace(
      /(^|[;\n}\)])\s*(?:export\s+)?(async\s+)?(function|class|const|let|var)\s+([A-Za-z_$][\w$]*)/g,
      (full, prefix, asyncKw = '', kind, name) => {
        if (!seen.has(name)) {
          seen.add(name);
          return full;
        }
        if (kind === 'function') return `${prefix}var ${name} = ${asyncKw || ''}function ${name}`;
        if (kind === 'class') return `${prefix}var ${name} = class ${name}`;
        // Replace const/let with var (allows redeclaration) instead of stripping the keyword
        // Stripping produced bare assignments that are invalid in strict mode / certain contexts
        return full.replace(/(const|let)\s+/, 'var ');
      },
    );
  } catch (e) {}
  return out;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function hasModuleSpecifier(code: string, specifier: string): boolean {
  if (!code) return false;
  const escaped = escapeRegExp(specifier);
  const pattern = new RegExp(`["']${escaped}(?:\\/[^"']*)?["']`);
  return pattern.test(code);
}

function extractVersionFromEsmUrl(urlValue: string | undefined): string | null {
  if (!urlValue) return null;
  const match = urlValue.match(/@(\d+(?:\.\d+){0,2}(?:-[0-9A-Za-z.-]+)?)/);
  return match?.[1] ?? null;
}

function splitBarePackageSpecifier(spec: string): { base: string; subpath: string } {
  const parts = spec.split('/');
  if (spec.startsWith('@')) {
    const base = parts.slice(0, 2).join('/');
    const subpath = parts.slice(2).join('/');
    return { base, subpath };
  }
  const base = parts[0];
  const subpath = parts.slice(1).join('/');
  return { base, subpath };
}

function toEsmPackageUrl(spec: string, version: string): string {
  const { base, subpath } = splitBarePackageSpecifier(spec);
  return `https://esm.sh/${base}@${version}${subpath ? `/${subpath}` : ''}`;
}

function normalizeVueRuntime(importMap: Record<string, string>, bundledJs: string): void {
  const hasVueRuntime =
    hasModuleSpecifier(bundledJs, 'vue') ||
    Object.keys(importMap).some(
      (k) => k === 'vue' || k.startsWith('vue/') || k.startsWith('@vue/'),
    );
  if (!hasVueRuntime) return;

  const vueVersion =
    extractVersionFromEsmUrl(importMap.vue) ||
    extractVersionFromEsmUrl(importMap['@vue/runtime-dom']) ||
    '3.5.13';
  const vueBase = `https://esm.sh/vue@${vueVersion}`;
  importMap.vue = vueBase;
  importMap['vue/'] = `${vueBase}/`;

  const vueRuntimeSpecs = [
    '@vue/runtime-dom',
    '@vue/runtime-core',
    '@vue/reactivity',
    '@vue/shared',
    '@vue/compiler-dom',
    '@vue/compiler-core',
  ];
  for (const spec of vueRuntimeSpecs) {
    importMap[spec] = toEsmPackageUrl(spec, vueVersion);
    const { base } = splitBarePackageSpecifier(spec);
    importMap[`${base}/`] = `${toEsmPackageUrl(base, vueVersion)}/`;
  }
}

function normalizeSvelteRuntime(importMap: Record<string, string>, bundledJs: string): void {
  const hasSvelteRuntime =
    hasModuleSpecifier(bundledJs, 'svelte') ||
    Object.keys(importMap).some((k) => k === 'svelte' || k.startsWith('svelte/'));
  if (!hasSvelteRuntime) return;

  // .svelte files are compiled with svelte@4 compiler in preview.
  // Keep runtime pinned to v4 to avoid missing export crashes from v5 internals.
  const svelteVersion = '4.2.19';
  const svelteBase = `https://esm.sh/svelte@${svelteVersion}`;
  importMap.svelte = svelteBase;
  importMap['svelte/'] = `${svelteBase}/`;
  // Point directly to CDN URL — no shim needed since svelte@4 has all expected exports
  // (append, detach, element, SvelteComponent, etc.). Previous data URL shim was broken
  // (used CommonJS `exports[key]` which doesn't work in ES modules) and data URLs get
  // filtered from the bare-specifier inlining step, leaving blob URL modules unable to
  // resolve "svelte/internal".
  importMap['svelte/internal'] = `${svelteBase}/internal`;
  importMap['svelte/internal/disclose-version'] = `${svelteBase}/internal/disclose-version`;
  importMap['svelte/store'] = `${svelteBase}/store`;
  importMap['svelte/motion'] = `${svelteBase}/motion`;
  importMap['svelte/transition'] = `${svelteBase}/transition`;
  importMap['svelte/animate'] = `${svelteBase}/animate`;
  importMap['svelte/easing'] = `${svelteBase}/easing`;
}

function normalizeAngularRuntime(
  importMap: Record<string, string>,
  bundledJs: string,
  hasAngularFlag: boolean,
): void {
  const angularSpecsInMap = Object.keys(importMap).filter((k) => k.startsWith('@angular/'));
  const hasAngularRuntime =
    hasAngularFlag ||
    hasModuleSpecifier(bundledJs, '@angular/core') ||
    hasModuleSpecifier(bundledJs, '@angular/common') ||
    angularSpecsInMap.length > 0;
  if (!hasAngularRuntime) return;

  const angularVersion =
    extractVersionFromEsmUrl(importMap['@angular/core']) ||
    extractVersionFromEsmUrl(importMap['@angular/common']) ||
    extractVersionFromEsmUrl(importMap['@angular/platform-browser']) ||
    null;
  if (!angularVersion) return;

  const commonAngularSpecs = new Set<string>([
    ...angularSpecsInMap.filter((spec) => !spec.endsWith('/')),
    '@angular/core',
    '@angular/common',
    '@angular/common/http',
    '@angular/compiler',
    '@angular/forms',
    '@angular/router',
    '@angular/platform-browser',
    '@angular/platform-browser/animations',
    '@angular/platform-browser-dynamic',
  ]);

  for (const spec of commonAngularSpecs) {
    importMap[spec] = toEsmPackageUrl(spec, angularVersion);
    const { base } = splitBarePackageSpecifier(spec);
    importMap[`${base}/`] = `${toEsmPackageUrl(base, angularVersion)}/`;
  }
}

function canonicalizeImportMapsInHtml(html: string): string {
  const importMapScriptRe =
    /<script[^>]*\btype\s*=\s*(?:"importmap(?:-shim)?"|'importmap(?:-shim)?'|importmap(?:-shim)?)\b[^>]*>([\s\S]*?)<\/script>/gi;
  const foundMaps: Array<{ body: string }> = [];
  let match: RegExpExecArray | null = null;
  while ((match = importMapScriptRe.exec(html)) !== null) {
    foundMaps.push({ body: match[1] || '' });
  }

  if (foundMaps.length <= 1) return html;

  const mergedImports: Record<string, string> = {};
  const mergedScopes: Record<string, Record<string, string>> = {};

  for (const map of foundMaps) {
    try {
      const parsed = JSON.parse(map.body || '{}');
      if (!parsed || typeof parsed !== 'object') continue;
      Object.assign(mergedImports, (parsed as any).imports || {});
      const scopes = (parsed as any).scopes || {};
      for (const [scopeKey, scopeMap] of Object.entries(scopes)) {
        if (!mergedScopes[scopeKey]) mergedScopes[scopeKey] = {};
        Object.assign(mergedScopes[scopeKey], (scopeMap as any) || {});
      }
    } catch {
      // Ignore malformed maps; keep valid entries.
    }
  }

  const canonicalMapJson = JSON.stringify(
    {
      imports: mergedImports,
      ...(Object.keys(mergedScopes).length > 0 ? { scopes: mergedScopes } : {}),
    },
    null,
    2,
  ).replace(/<\//g, '<\\/');

  const canonicalMapTag = `<script type="importmap">\n${canonicalMapJson}\n</script>`;

  let replacedOne = false;
  return html.replace(importMapScriptRe, () => {
    if (replacedOne) return '';
    replacedOne = true;
    return canonicalMapTag;
  });
}

export function assembleHtml(options: AssembleOptions): string {
  // Clear module-level state from previous invocations
  shimmedSpecifiers.length = 0;

  // Determine if this framework needs React. Only React/Next.js need the full
  // React import map, shims, and bootstrap. Other frameworks (vanilla, vue, svelte,
  // angular) should NOT load React at all.
  const fw = options.framework || 'react'; // default to react for backward compat
  const needsReact = fw === 'react' || fw === 'next' || fw === 'unknown' || !fw;

  let html = options.indexHtml;

  // Remove <base> and <meta refresh> tags that could cause inception
  html = html.replace(/<base[^>]*>/gi, '');
  html = html.replace(/<meta[^>]*http-equiv\s*=\s*["']refresh["'][^>]*>/gi, '');
  // Remove CSP meta from session HTML so preview policy is controlled by the bundler.
  html = html.replace(/<meta[^>]*http-equiv\s*=\s*["']Content-Security-Policy["'][^>]*>/gi, '');

  // Ensure common SPA mount points exist. User projects may target either #root or #app,
  // and missing one leads to a silent white screen with "Root element ... not found".
  const hasRootMount = /\bid\s*=\s*["']root["']/i.test(html);
  const hasAppMount = /\bid\s*=\s*["']app["']/i.test(html);
  if (!hasRootMount || !hasAppMount) {
    const mountsToAdd: string[] = [];
    if (!hasRootMount) mountsToAdd.push('<div id="root"></div>');
    if (!hasAppMount) mountsToAdd.push('<div id="app"></div>');
    const mountMarkup = mountsToAdd.join('\n');
    if (html.includes('</body>')) {
      html = html.replace(
        '</body>',
        `${mountMarkup}
</body>`,
      );
    } else {
      html += `
${mountMarkup}`;
    }
  }

  // Remove all source <script src=...> tags from session HTML.
  // For bundled previews, these scripts are either stale app assets (e.g. /_assets/index-*.js)
  // or duplicate runtime entrypoints. Keeping them can load an unrelated app shell and cause
  // module/export mismatches ("react does not provide default") before our bundled module runs.
  html = html.replace(
    /<script[^>]*\bsrc\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)[^>]*>\s*<\/script>/gi,
    '',
  );
  // Also remove self-closing variants
  html = html.replace(/<script[^>]*\bsrc\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)[^>]*\/>/gi, '');

  // Remove ALL inline <script type="module"> (quoted or unquoted) from session index.html.
  // Guarantee: the only module script in the final srcdoc is the one we inject below (bundledJs),
  // which runs after the import map. Otherwise a bare "react-dom/client" import would run
  // without the map and throw "Failed to resolve module specifier".
  html = html.replace(
    /<script[^>]*\btype\s*=\s*(?:"module"|'module'|module)\b[^>]*>[\s\S]*?<\/script>/gi,
    '',
  );
  // Remove existing import maps from session HTML so only our generated map is active.
  // Multiple maps or user-provided maps can remap "react" incorrectly (e.g. missing createContext).
  html = html.replace(
    /<script[^>]*\btype\s*=\s*(?:"importmap"|'importmap'|importmap)\b[^>]*>[\s\S]*?<\/script>/gi,
    '',
  );
  // Remove importmap-shim variants too (es-module-shims setups), otherwise duplicate/
  // conflicting rules can still be applied in srcdoc and break React hook resolution.
  html = html.replace(
    /<script[^>]*\btype\s*=\s*(?:"importmap-shim"|'importmap-shim'|importmap-shim)\b[^>]*>[\s\S]*?<\/script>/gi,
    '',
  );
  // Remove es-module-shims loader scripts from source HTML in preview mode.
  html = html.replace(
    /<script[^>]*\bsrc\s*=\s*(?:"[^"]*es-module-shims[^"]*"|'[^']*es-module-shims[^']*'|[^\s>]*es-module-shims[^\s>]*)[^>]*>\s*<\/script>/gi,
    '',
  );

  // Remove original <link rel="stylesheet" href="..."> tags (CSS is bundled)
  html = html.replace(/<link[^>]*rel\s*=\s*["']stylesheet["'][^>]*>/gi, '');

  // Remove <link rel="modulepreload"> tags (not needed in preview)
  html = html.replace(/<link[^>]*rel\s*=\s*["']modulepreload["'][^>]*>/gi, '');

  const importMap = { ...options.importMap };

  // Only inject React 19 fallbacks, shims, and JSX runtime for React/Next.js frameworks.
  // Vanilla, Vue, Svelte, Angular etc. should NOT load React at all.
  if (needsReact) {
    const REACT_19_FALLBACKS: Record<string, string> = {
      react: 'https://esm.sh/react@19.2.4',
      'react-dom': 'https://esm.sh/react-dom@19.2.4?external=react',
      'react-dom/client': 'https://esm.sh/react-dom@19.2.4/client?external=react',
    };

    const isPreactCompat =
      /preact/i.test(importMap.react || '') || /preact/i.test(importMap['react-dom'] || '');
    // Force a single React source for preview unless this is an intentional preact compat map.
    // This prevents stale/conflicting user maps from causing dual/misaligned React instances.
    if (!isPreactCompat) {
      for (const [k, v] of Object.entries(REACT_19_FALLBACKS)) {
        importMap[k] = v;
      }
    } else {
      for (const [k, v] of Object.entries(REACT_19_FALLBACKS)) {
        if (!importMap[k]) importMap[k] = v;
      }
    }
    // Strip ?exports=... from React URL
    if (importMap.react) {
      try {
        const targetUrl = new URL(importMap.react);
        if (targetUrl.searchParams.has('exports')) {
          targetUrl.searchParams.delete('exports');
          importMap.react = targetUrl.toString();
        }
      } catch {
        // Non-URL specifier — keep as-is.
      }
    }

    // Add explicit react/jsx-runtime entries for esbuild automatic JSX transform.
    const reactUrl = importMap.react || 'https://esm.sh/react@19.2.4';
    if (!importMap['react/jsx-runtime']) {
      importMap['react/jsx-runtime'] = `${reactUrl}/jsx-runtime`;
    }
    if (!importMap['react/jsx-dev-runtime']) {
      importMap['react/jsx-dev-runtime'] = `${reactUrl}/jsx-dev-runtime`;
    }
  }

  // Save original CDN URLs BEFORE creating data URL shims.
  // The URL inlining step resolves bare specifiers in bundled JS to absolute CDN URLs
  // for blob URL modules (which can't use import maps). Shim data URLs would break
  // inside blob contexts, so we inline the original CDN URLs instead.
  // NOTE: svelte/internal and other shimmed modules will be added after shim creation
  const urlInliningMap: Record<string, string> = { ...importMap };
  // Track which specifiers need shim URLs for inlining
  // Uses module-level shimmedSpecifiers array (declared at top of file) to avoid shadowing

  // lucide-react compatibility shim: Only for React frameworks (uses React for fallback SVG).
  // Re-exports ALL icons from the CDN module using proper ESM syntax.
  // IMPORTANT: Data URL modules are ESM — they do NOT have a CJS `exports` object.
  // Previous version used `exports[key] = ...` which silently failed, breaking all icons.
  // Now uses `export * from "..."` to re-export everything, with named overrides for fallbacks.
  const lucideTarget = importMap['lucide-react'];
  if (needsReact && lucideTarget) {
    const lucideShimSource = [
      `import * as LucideNS from "${lucideTarget}";`,
      // Re-export ALL icons from the CDN module (proper ESM re-export)
      `export * from "${lucideTarget}";`,
      // Also export the namespace as default
      'export default LucideNS;',
      // Fallback icon component for truly missing icons
      'const _fallbackSvg = (props) => {',
      '  const size = props?.size || 16;',
      '  const React = globalThis.__TASKING_React || { createElement: () => null };',
      '  return React.createElement("svg", {',
      '    width: size, height: size, viewBox: "0 0 24 24",',
      '    fill: "none", stroke: "currentColor", strokeWidth: 2,',
      '    strokeLinecap: "round", strokeLinejoin: "round"',
      '  }, React.createElement("rect", { x: 3, y: 3, width: 18, height: 18, rx: 2 }));',
      '};',
      // Named exports for icons that may not exist in the CDN build.
      // These override the star re-export for the same name (ESM spec: local wins).
      'export const LayoutDashboard = LucideNS.LayoutDashboard || LucideNS.Layout || _fallbackSvg;',
      'export const Dashboard = LucideNS.Dashboard || LucideNS.LayoutDashboard || LucideNS.Layout || _fallbackSvg;',
      'export const Sort = LucideNS.Sort || LucideNS.ArrowUpDown || LucideNS.ChevronsUpDown || _fallbackSvg;',
      'export const PanelLeft = LucideNS.PanelLeft || LucideNS.Sidebar || LucideNS.Layout || _fallbackSvg;',
      'export const LayoutGrid = LucideNS.LayoutGrid || LucideNS.Grid || LucideNS.Layout || _fallbackSvg;',
      'export const Loading = LucideNS.Loading || LucideNS.Loader2 || LucideNS.Loader || _fallbackSvg;',
      'export const LoadingSpinner = LucideNS.LoadingSpinner || LucideNS.Loader2 || LucideNS.Loader || _fallbackSvg;',
      'export const Success = LucideNS.Success || LucideNS.CheckCircle || LucideNS.Check || _fallbackSvg;',
      'export const Gear = LucideNS.Gear || LucideNS.Settings || LucideNS.Cog || _fallbackSvg;',
      'export const Gamepad2 = LucideNS.Gamepad2 || LucideNS.Gamepad || _fallbackSvg;',
      'export const Gamepad = LucideNS.Gamepad || LucideNS.Gamepad2 || _fallbackSvg;',
    ].join('\n');
    const lucideShimUrl = `data:text/javascript;charset=utf-8,${encodeURIComponent(lucideShimSource)}`;
    importMap['lucide-react'] = lucideShimUrl;
    shimmedSpecifiers.push('lucide-react');
  }

  // @radix-ui/react-slot shim: replaces the CDN version which has a React 19 infinite
  // re-render bug. Only needed for React frameworks.
  if (needsReact) {
    // CRITICAL: Use globalThis.__TASKING_React instead of CDN import to prevent dual-React (#185).
    // Data URL modules that import React from a CDN URL load a SEPARATE React instance
    // (different module identity than the one pre-loaded by the bootstrap), causing
    // "Minified React error #185: two copies of React running simultaneously".
    const radixSlotShimSource = `
var React = globalThis.__TASKING_React || { isValidElement: () => false, cloneElement: (c) => c, createElement: (...a) => null, useCallback: (f) => f, useRef: (v) => ({ current: v }) };
// Safe Slot implementation — no composeRefs, no React 19 re-render loop.
// Handles asChild pattern: merges props onto the single child element.
function Slot(props) {
  var children = props.children;
  var slotProps = Object.assign({}, props);
  delete slotProps.children;
  if (React.isValidElement(children)) {
    var childProps = children.props || {};
    var mergedClass = [slotProps.className, childProps.className].filter(Boolean).join(' ') || undefined;
    var mergedStyle = slotProps.style || childProps.style ? Object.assign({}, slotProps.style, childProps.style) : undefined;
    var merged = Object.assign({}, slotProps, childProps);
    if (mergedClass !== undefined) merged.className = mergedClass;
    if (mergedStyle !== undefined) merged.style = mergedStyle;
    return React.cloneElement(children, merged);
  }
  return children != null ? children : null;
}
Slot.displayName = 'Slot';
function Slottable(props) { return props.children; }
Slottable.displayName = 'Slottable';
var _composedRefs = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
var _fnRefLastValue = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
var _refDepth = 0;
function _setRef(ref, value) {
  if (typeof ref === 'function') {
    if (value !== null && value !== undefined && typeof value === 'object' && _fnRefLastValue) {
      var lastWR = _fnRefLastValue.get(ref);
      if (lastWR) { var lastVal = lastWR.deref(); if (lastVal === value) return; }
      _fnRefLastValue.set(ref, new WeakRef(value));
    } else if (value === null && _fnRefLastValue) {
      _fnRefLastValue.delete(ref);
    }
    // Inside a composed ref chain: defer leaf function refs (state setters) to microtask
    // to avoid incrementing React's nestedUpdateCount during the commit phase.
    // Composed ref callbacks are called synchronously so inner object refs are set immediately.
    if (_refDepth > 0 && _composedRefs && !_composedRefs.has(ref)) {
      var fn = ref, val = value;
      queueMicrotask(function() { fn(val); });
    } else {
      ref(value);
    }
  }
  else if (ref && typeof ref === 'object') { try { ref.current = value; } catch(e) {} }
}
var _composeCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
function composeRefs() {
  var refs = Array.prototype.slice.call(arguments);
  var cacheKey = null;
  for (var i = refs.length - 1; i >= 0; i--) {
    var r = refs[i];
    if (r != null && typeof r === 'object') { cacheKey = r; break; }
  }
  if (!cacheKey) {
    for (var i = refs.length - 1; i >= 0; i--) {
      var r = refs[i];
      if (r != null && typeof r === 'function') { cacheKey = r; break; }
    }
  }
  if (cacheKey && _composeCache) {
    var cached = _composeCache.get(cacheKey);
    if (cached) { cached.latestRefs = refs; return cached.fn; }
    var entry = { latestRefs: refs, lastNode: undefined, fn: null };
    entry.fn = function stableComposedRef(node) {
      if (node !== null && node === entry.lastNode) return;
      if (node !== null) entry.lastNode = node;
      _refDepth++;
      try { entry.latestRefs.forEach(function(ref) { _setRef(ref, node); }); }
      finally { _refDepth--; }
    };
    if (_composedRefs) _composedRefs.add(entry.fn);
    _composeCache.set(cacheKey, entry);
    return entry.fn;
  }
  var fallback = function composedRef(node) {
    _refDepth++;
    try { refs.forEach(function(ref) { _setRef(ref, node); }); }
    finally { _refDepth--; }
  };
  if (_composedRefs) _composedRefs.add(fallback);
  return fallback;
}
function useComposedRefs() {
  var refs = Array.prototype.slice.call(arguments);
  var refsRef = React.useRef(refs);
  refsRef.current = refs;
  var lastNodeRef = React.useRef(null);
  var cb = React.useCallback(function(node) {
    if (node !== null && node === lastNodeRef.current) return;
    if (node !== null) lastNodeRef.current = node;
    _refDepth++;
    try {
      var currentRefs = refsRef.current;
      for (var i = 0; i < currentRefs.length; i++) { _setRef(currentRefs[i], node); }
    } finally { _refDepth--; }
  }, []);
  if (_composedRefs) _composedRefs.add(cb);
  return cb;
}
export { Slot, Slot as Root, Slottable, composeRefs, useComposedRefs };
export default Slot;
`;
    const radixSlotShimUrl = `data:text/javascript;charset=utf-8,${encodeURIComponent(radixSlotShimSource)}`;
    importMap['@radix-ui/react-slot'] = radixSlotShimUrl;
    importMap['@radix-ui/react-compose-refs'] = radixSlotShimUrl;
    shimmedSpecifiers.push('@radix-ui/react-slot');
    shimmedSpecifiers.push('@radix-ui/react-compose-refs');
  }

  // ── React-specific shims (next-themes, react-router-dom, styled-components, emotion) ──
  // Only injected for React/Next.js frameworks. Non-React frameworks skip all of these.
  if (needsReact) {
    // CRITICAL: Use globalThis.__TASKING_React instead of CDN import to prevent dual-React (#185).
    const themeProviderShimSource = [
      'const React = globalThis.__TASKING_React || { createContext: () => ({ Provider: (p) => p.children }), useState: (v) => [v, () => {}], useEffect: () => {}, useContext: () => null, createElement: (...a) => null };',
      'const ThemeContext = React.createContext({ theme: "dark", setTheme: function() {}, resolvedTheme: "dark" });',
      'export function ThemeProvider(props) {',
      '  var children = props.children; var defaultTheme = props.defaultTheme || "dark";',
      '  var storageKey = props.storageKey || "theme";',
      '  var enableSystem = props.enableSystem !== false;',
      '  var state = React.useState(defaultTheme);',
      '  var theme = state[0]; var setTheme = state[1];',
      '  var resolvedTheme = theme === "system" ? (enableSystem && typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : theme;',
      '  var value = { theme: theme, setTheme: setTheme, resolvedTheme: resolvedTheme, systemTheme: enableSystem ? (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : undefined };',
      '  React.useEffect(function() {',
      '    var root = document.documentElement;',
      '    root.classList.remove("light", "dark");',
      '    root.classList.add(resolvedTheme);',
      '    try { localStorage.setItem(storageKey, theme); } catch(e) {}',
      '  }, [theme, resolvedTheme, storageKey]);',
      '  return React.createElement(ThemeContext.Provider, { value: value }, children);',
      '}',
      'export function useTheme() {',
      '  var ctx = React.useContext(ThemeContext);',
      '  return ctx || { theme: "dark", setTheme: function() {}, resolvedTheme: "dark", systemTheme: "dark" };',
      '}',
      'export default ThemeProvider;',
    ].join('\n');
    const themeProviderShimUrl = `data:text/javascript;charset=utf-8,${encodeURIComponent(themeProviderShimSource)}`;
    importMap['next-themes'] = themeProviderShimUrl;
    importMap['next-themes/react'] = themeProviderShimUrl;

    // react-router-dom shim: ensure BrowserRouter and other common router exports
    // are always available even when the CDN module has missing or renamed exports
    // (e.g. version mismatch between v6 and v7). Provides identity-function fallbacks.
    // Hooks are wrapped in try/catch so they gracefully fall back to stubs when
    // called outside a <Router> context (common in AI-generated preview code).
    const routerTarget = importMap['react-router-dom'];
    if (routerTarget && !routerTarget.startsWith('data:')) {
      // CRITICAL: Use globalThis.__TASKING_React instead of CDN import to prevent dual-React (#185).
      // Data URL modules that import React from a CDN URL load a SEPARATE React instance,
      // causing "Minified React error #185: two copies of React running simultaneously".
      const routerShimSource = `
import * as _RR from "${routerTarget}";
const React = globalThis.__TASKING_React || { useContext: () => null, createElement: (...a) => null };
// Re-export ALL exports from the original module (proper ESM).
// Named exports below override star re-exports for the same name (ESM spec).
// This ensures unlisted exports like matchRoutes, useHref, UNSAFE_NavigationContext, etc. are available.
export * from "${routerTarget}";
const _id = (p) => p?.children || null;
// Safe hook wrapper
const _safeHook = (hook, fallback) => (...args) => { try { return hook(...args); } catch(e) { return fallback(...args); } };
// Idempotent router wrapper: if already inside a Router context, just render children.
// Safety-net for legacy/existing sessions where main.tsx still wraps App in BrowserRouter.
// New sessions should have BrowserRouter only in App.tsx (not main.tsx).
const _wrapRouter = (RouterComp) => {
  if (!RouterComp) return _id;
  const Wrapped = (props) => {
    try {
      const ctx = _RR.UNSAFE_NavigationContext;
      if (ctx) {
        const val = React.useContext(ctx);
        if (val && val.navigator) return props.children || null;
      }
    } catch(e) {}
    return React.createElement(RouterComp, props);
  };
  Wrapped.displayName = RouterComp.displayName || RouterComp.name || 'Router';
  return Wrapped;
};
// Define our exports first
const _exports = {};
// In srcdoc iframes, BrowserRouter's pushState throws SecurityError.
// Swap BrowserRouter → MemoryRouter so clicks actually navigate.
const _isSrcdoc = typeof window !== 'undefined' && window.location.href === 'about:srcdoc';
_exports.BrowserRouter = _isSrcdoc ? _wrapRouter(_RR.MemoryRouter) : _wrapRouter(_RR.BrowserRouter);
_exports.HashRouter = _wrapRouter(_RR.HashRouter);
_exports.MemoryRouter = _wrapRouter(_RR.MemoryRouter);
// React Router v7's Route() unconditionally throws when rendered directly
// (the error fires during React's render phase, not during the function call).
// <Routes> reads Route children's props WITHOUT calling Route(), so correct code works.
// But AI-generated code often renders <Route> outside <Routes>, which crashes.
// Fix: Replace Route with a safe function. Do NOT Object.assign from _RR.Route —
// that copies internal prototype/symbols that can make React invoke the original thrower.
function SafeRoute(props) {
  return props?.element || (typeof props?.Component === 'function' ? React.createElement(props.Component) : null) || props?.children || null;
}
SafeRoute.displayName = 'Route';
_exports.Route = SafeRoute;
// Wrap Routes with an error boundary to catch render-phase type-check failures.
// React.createElement() doesn't throw — the error fires during React's render phase,
// so a try/catch around createElement never catches it. Use a class-based ErrorBoundary.
const _OrigRoutes = _RR.Routes;
function _routesFallback(props) {
  const kids = React.Children.toArray(props?.children || []);
  for (const kid of kids) {
    if (kid && typeof kid === 'object' && 'props' in kid) {
      const el = kid.props?.element || (typeof kid.props?.Component === 'function' ? React.createElement(kid.props.Component) : null);
      if (el) return el;
    }
  }
  return kids[0] || null;
}
let _RoutesBoundary;
if (_OrigRoutes && React.Component) {
  class _RB extends React.Component {
    constructor(p) { super(p); this.state = { err: false }; }
    static getDerivedStateFromError() { return { err: true }; }
    render() {
      if (this.state.err) return _routesFallback(this.props);
      return React.createElement(_OrigRoutes, this.props);
    }
  }
  _RoutesBoundary = _RB;
}
_exports.Routes = _RoutesBoundary ? (props) => React.createElement(_RoutesBoundary, props) : (_OrigRoutes || _id);
_exports.Link = _RR.Link || ((p) => React.createElement("a", {href: p?.to}, p?.children));
_exports.NavLink = _RR.NavLink || _exports.Link;
_exports.Navigate = _RR.Navigate || (() => null);
_exports.Outlet = _RR.Outlet || _id;
_exports.RouterProvider = _RR.RouterProvider || _id;
_exports.createBrowserRouter = _RR.createBrowserRouter || ((r) => ({routes: r}));
_exports.createHashRouter = _RR.createHashRouter || ((r) => ({routes: r}));
_exports.createMemoryRouter = _RR.createMemoryRouter || ((r) => ({routes: r}));
_exports.createRoutesFromElements = _RR.createRoutesFromElements || ((e) => []);
// Hooks with fallbacks
_exports.useNavigate = _RR.useNavigate ? _safeHook(_RR.useNavigate, () => () => {}) : () => () => {};
_exports.useLocation = _RR.useLocation ? _safeHook(_RR.useLocation, () => ({pathname: "/", search: "", hash: "", state: null})) : () => ({pathname: "/", search: "", hash: "", state: null});
_exports.useParams = _RR.useParams ? _safeHook(_RR.useParams, () => ({})) : () => ({});
_exports.useSearchParams = _RR.useSearchParams ? _safeHook(_RR.useSearchParams, () => [new URLSearchParams(), () => {}]) : () => [new URLSearchParams(), () => {}];
_exports.useRoutes = _RR.useRoutes ? _safeHook(_RR.useRoutes, () => null) : () => null;
_exports.useMatch = _RR.useMatch ? _safeHook(_RR.useMatch, () => null) : () => null;
_exports.useOutlet = _RR.useOutlet ? _safeHook(_RR.useOutlet, () => null) : () => null;
_exports.useOutletContext = _RR.useOutletContext ? _safeHook(_RR.useOutletContext, () => undefined) : () => undefined;
_exports.default = _RR.default || _RR;
// Named exports override the star re-export for overridden names (ESM spec: local wins).
// Everything else (matchRoutes, useHref, UNSAFE_NavigationContext, etc.) flows through export *.
export const { BrowserRouter, HashRouter, MemoryRouter, Routes, Route, Link, NavLink, Navigate, Outlet, RouterProvider, createBrowserRouter, createHashRouter, createMemoryRouter, createRoutesFromElements, useNavigate, useLocation, useParams, useSearchParams, useRoutes, useMatch, useOutlet, useOutletContext } = _exports;
export default _exports.default;
`;
      const routerShimUrl = `data:text/javascript;charset=utf-8,${encodeURIComponent(routerShimSource)}`;
      importMap['react-router-dom'] = routerShimUrl;
      shimmedSpecifiers.push('react-router-dom');
    }

    // styled-components shim: provide a lightweight implementation that always works.
    // The CDN module often fails with "styled.div is not a function" due to default export
    // issues or React context mismatches. This shim implements the core tagged-template API
    // using inline styles + className injection so it works reliably in preview iframes.
    // CRITICAL: Use globalThis.__TASKING_React (NOT a CDN import) to avoid dual-React
    // instances. Data: URL modules can't use the import map, so `import from "react"`
    // would load a separate React instance → useState is not a function.
    {
      const styledShimSource = `
const React = globalThis.__TASKING_React || globalThis.__TASKING_React?.default || {};

// Unique class counter for generated CSS
let _sc_counter = 0;
const _sc_styleSheet = typeof document !== 'undefined' ? (() => {
  const el = document.createElement('style');
  el.id = '__tasking-styled-components';
  document.head.appendChild(el);
  return el.sheet;
})() : null;

// Parse tagged template literal with interpolations
function _parseTemplate(strings, args, props) {
  let css = '';
  for (let i = 0; i < strings.length; i++) {
    css += strings[i];
    if (i < args.length) {
      const arg = args[i];
      if (typeof arg === 'function') {
        const result = arg(props || {});
        css += result == null ? '' : String(result);
      } else {
        css += arg == null ? '' : String(arg);
      }
    }
  }
  return css;
}

// Insert CSS rule and return the generated class name
function _insertCss(cssText) {
  const className = 'sc-' + (++_sc_counter).toString(36);
  if (_sc_styleSheet) {
    try { _sc_styleSheet.insertRule('.' + className + ' { ' + cssText + ' }', _sc_styleSheet.cssRules.length); }
    catch(e) {
      // Fallback: insert via textContent for complex CSS (nested, media queries, etc.)
      const styleEl = _sc_styleSheet.ownerNode;
      if (styleEl) styleEl.textContent += '\\n.' + className + ' { ' + cssText + ' }';
    }
  }
  return className;
}

// Core factory: creates a styled component for a given tag or component
function _createStyled(tag) {
  // Support styled(Component) and styled.div\`...\` (tagged template)
  const factory = function(strings, ...args) {
    if (Array.isArray(strings)) {
      // Tagged template: styled.div\`color: red;\`
      const StyledComp = React.forwardRef(function(props, ref) {
        const { className: userClass, as: asProp, ...rest } = props || {};
        const cssText = _parseTemplate(strings, args, props);
        const genClass = _insertCss(cssText);
        const finalClass = userClass ? genClass + ' ' + userClass : genClass;
        const resolvedTag = asProp || tag;
        if (typeof resolvedTag === 'string') {
          // Filter out non-DOM props for HTML elements
          const domProps = {};
          for (const k of Object.keys(rest)) {
            if (k === 'theme' || k.startsWith('$')) continue;
            domProps[k] = rest[k];
          }
          return React.createElement(resolvedTag, { ...domProps, ref: ref, className: finalClass });
        }
        return React.createElement(resolvedTag, { ...rest, ref: ref, className: finalClass });
      });
      StyledComp.displayName = 'styled(' + (typeof tag === 'string' ? tag : tag?.displayName || tag?.name || 'Component') + ')';
      // Allow .attrs() chaining on result
      StyledComp.attrs = (attrFn) => {
        const WithAttrs = React.forwardRef(function(props, ref) {
          const extra = typeof attrFn === 'function' ? attrFn(props) : attrFn;
          return React.createElement(StyledComp, { ...extra, ...props, ref: ref });
        });
        WithAttrs.displayName = StyledComp.displayName;
        return WithAttrs;
      };
      return StyledComp;
    }
    // styled(Component)({ ...config }) — object syntax (rare but valid)
    if (typeof strings === 'object' && !Array.isArray(strings)) {
      const cssObj = strings;
      const StyledObj = React.forwardRef(function(props, ref) {
        const style = typeof cssObj === 'function' ? cssObj(props) : cssObj;
        return React.createElement(tag, { ...props, ref: ref, style: { ...(props?.style || {}), ...style } });
      });
      return StyledObj;
    }
    return React.createElement(tag, strings);
  };
  // .attrs() before template
  factory.attrs = (attrFn) => {
    const innerFactory = function(strings2, ...args2) {
      const Base = factory(strings2, ...args2);
      const WithAttrs = React.forwardRef(function(props, ref) {
        const extra = typeof attrFn === 'function' ? attrFn(props) : attrFn;
        return React.createElement(Base, { ...extra, ...props, ref: ref });
      });
      WithAttrs.displayName = Base.displayName;
      return WithAttrs;
    };
    return innerFactory;
  };
  return factory;
}

// HTML element names for styled.div, styled.span, etc.
const _elements = [
  'a','abbr','address','area','article','aside','audio','b','base','bdi','bdo','big',
  'blockquote','body','br','button','canvas','caption','cite','code','col','colgroup',
  'data','datalist','dd','del','details','dfn','dialog','div','dl','dt','em','embed',
  'fieldset','figcaption','figure','footer','form','h1','h2','h3','h4','h5','h6',
  'head','header','hgroup','hr','html','i','iframe','img','input','ins','kbd','label',
  'legend','li','link','main','map','mark','menu','menuitem','meta','meter','nav',
  'noscript','object','ol','optgroup','option','output','p','param','picture','pre',
  'progress','q','rp','rt','ruby','s','samp','script','section','select','small',
  'source','span','strong','style','sub','summary','sup','table','tbody','td','textarea',
  'tfoot','th','thead','time','title','tr','track','u','ul','var','video','wbr',
  'circle','clipPath','defs','ellipse','foreignObject','g','image','line','linearGradient',
  'mask','path','pattern','polygon','polyline','radialGradient','rect','stop','svg',
  'text','tspan'
];

// Build the styled function/proxy
const styled = function(tag) { return _createStyled(tag); };
for (const el of _elements) { styled[el] = _createStyled(el); }

// css helper
export function css(strings, ...args) {
  if (Array.isArray(strings)) return _parseTemplate(strings, args, {});
  return strings;
}

// keyframes helper
export function keyframes(strings, ...args) {
  const name = 'sc-kf-' + (++_sc_counter).toString(36);
  const body = Array.isArray(strings) ? _parseTemplate(strings, args, {}) : String(strings);
  if (_sc_styleSheet) {
    try { _sc_styleSheet.insertRule('@keyframes ' + name + ' { ' + body + ' }', _sc_styleSheet.cssRules.length); }
    catch(e) {
      const styleEl = _sc_styleSheet.ownerNode;
      if (styleEl) styleEl.textContent += '\\n@keyframes ' + name + ' { ' + body + ' }';
    }
  }
  return name;
}

// createGlobalStyle
export function createGlobalStyle(strings, ...args) {
  return function GlobalStyle(props) {
    React.useEffect(function() {
      const cssText = _parseTemplate(strings, args, props || {});
      const el = document.createElement('style');
      el.setAttribute('data-sc-global', 'true');
      el.textContent = cssText;
      document.head.appendChild(el);
      return function() { el.remove(); };
    }, []);
    return null;
  };
}

// ThemeProvider + useTheme
const ThemeContext = React.createContext({});
export function ThemeProvider(props) {
  return React.createElement(ThemeContext.Provider, { value: props.theme || {} }, props.children);
}
export function useTheme() { return React.useContext(ThemeContext) || {}; }
export function withTheme(Comp) {
  return function(props) {
    const theme = React.useContext(ThemeContext);
    return React.createElement(Comp, { ...props, theme: theme });
  };
}

// ServerStyleSheet (no-op for client)
export class ServerStyleSheet {
  collectStyles(children) { return children; }
  getStyleTags() { return ''; }
  getStyleElement() { return []; }
  seal() {}
}

// isStyledComponent check
export function isStyledComponent(target) { return false; }

// StyleSheetManager (no-op wrapper)
export function StyleSheetManager(props) { return props.children || null; }

export default styled;
export { styled };
`;
      const styledShimUrl = `data:text/javascript;charset=utf-8,${encodeURIComponent(styledShimSource)}`;
      importMap['styled-components'] = styledShimUrl;
      shimmedSpecifiers.push('styled-components');
      // Also cover @emotion/styled which uses the same styled.div`` API
      importMap['@emotion/styled'] = styledShimUrl;
      shimmedSpecifiers.push('@emotion/styled');
    }

    // @emotion/react shim: provides css prop, Global, and keyframes helpers
    // CRITICAL: Use globalThis.__TASKING_React to avoid dual React instances.
    {
      const emotionReactShimSource = `
const React = globalThis.__TASKING_React || globalThis.__TASKING_React?.default || {};
export function css(strings, ...args) {
  if (Array.isArray(strings)) {
    let result = '';
    for (let i = 0; i < strings.length; i++) {
      result += strings[i];
      if (i < args.length) result += args[i] == null ? '' : String(args[i]);
    }
    return result;
  }
  return strings;
}
export function Global(props) {
  React.useEffect(function() {
    const cssText = typeof props.styles === 'function' ? props.styles({}) : (props.styles || '');
    const el = document.createElement('style');
    el.setAttribute('data-emotion-global', 'true');
    el.textContent = typeof cssText === 'string' ? cssText : '';
    document.head.appendChild(el);
    return function() { el.remove(); };
  }, []);
  return null;
}
export function keyframes(strings, ...args) {
  const name = 'em-kf-' + Math.random().toString(36).slice(2, 8);
  const body = Array.isArray(strings) ? css(strings, ...args) : String(strings);
  if (typeof document !== 'undefined') {
    const el = document.createElement('style');
    el.textContent = '@keyframes ' + name + ' { ' + body + ' }';
    document.head.appendChild(el);
  }
  return name;
}
export function ClassNames(props) {
  const fn = props.children;
  if (typeof fn === 'function') return fn({ css: css, cx: function() { return Array.from(arguments).filter(Boolean).join(' '); } });
  return fn || null;
}
const ThemeContext = React.createContext({});
export function ThemeProvider(props) { return React.createElement(ThemeContext.Provider, { value: props.theme || {} }, props.children); }
export function useTheme() { return React.useContext(ThemeContext) || {}; }
export function withTheme(Comp) { return function(props) { return React.createElement(Comp, { ...props, theme: React.useContext(ThemeContext) }); }; }
export function jsx(type, props, ...children) { return React.createElement(type, props, ...children); }
export function jsxs(type, props, ...children) { return React.createElement(type, props, ...children); }
export const Fragment = React.Fragment;
export const CacheProvider = function(props) { return props.children || null; };
export function createCache() { return { key: 'css', nonce: undefined, inserted: {}, registered: {}, sheet: { tags: [] } }; }
`;
      const emotionReactShimUrl = `data:text/javascript;charset=utf-8,${encodeURIComponent(emotionReactShimSource)}`;
      importMap['@emotion/react'] = emotionReactShimUrl;
      importMap['@emotion/react/jsx-runtime'] = emotionReactShimUrl;
      shimmedSpecifiers.push('@emotion/react');
      shimmedSpecifiers.push('@emotion/react/jsx-runtime');
    }
  } // end needsReact shims

  // Framework runtime compatibility locks:
  // Keep compiler/runtime versions aligned for frameworks that compile in-browser.
  normalizeVueRuntime(importMap, options.bundledJs);
  normalizeSvelteRuntime(importMap, options.bundledJs);
  normalizeAngularRuntime(importMap, options.bundledJs, Boolean(options.hasAngular));

  // Sync framework-normalized entries to the inlining map.
  // Framework normalization may have added/changed entries (e.g. svelte/internal, vue, etc.)
  // that weren't in the original snapshot.
  // IMPORTANT: Do NOT overwrite original CDN URLs with data: URL shims.
  // urlInliningMap is used to inline absolute CDN URLs into blob URL module code.
  // Blob URL modules can't use import maps, so they need real CDN URLs, not data: shims.
  // The data: URL shims remain in the HTML import map for regular module scripts.
  for (const [spec, url] of Object.entries(importMap)) {
    // Skip data: URL shims if we already have the original CDN URL
    if (
      url.startsWith('data:') &&
      urlInliningMap[spec] &&
      !urlInliningMap[spec].startsWith('data:')
    ) {
      continue;
    }
    if (!urlInliningMap[spec] || urlInliningMap[spec] !== url) {
      urlInliningMap[spec] = url;
    }
  }

  // Escape </ sequences in the import map JSON to prevent the HTML parser from
  // seeing </script> patterns inside data URLs or other import map values.
  const importMapJson = JSON.stringify({ imports: importMap }, null, 2).replace(/<\//g, '<\\/');
  const importMapTag = `<script type="importmap">
${importMapJson}
</script>`;

  // When Tailwind browser script is active, strip raw @import "tailwindcss" from bundled CSS
  // since the browser script processes utility classes directly
  let processedCss = options.bundledCss;
  if (options.hasTailwind) {
    console.debug(
      '[assembler] bundledCss length:',
      processedCss?.length ?? 0,
      processedCss?.trim()
        ? '(has content)'
        : '(EMPTY — CSS variables will use defaults from TAILWIND_DESIGN_BASELINE)',
    );
  }
  if (options.hasTailwind && processedCss) {
    // Strip raw Tailwind @imports from user CSS. The baseline already injects a dedicated
    // Tailwind entry style block, so leaving these in can trigger bad relative fetches like
    // /session/tailwindcss inside srcdoc contexts.
    processedCss = processedCss.replace(/@import\s+["']tailwindcss(?:\/[\w-]+)?["'];?/g, '');
    processedCss = processedCss.replace(
      /@import\s+url\(\s*["']?tailwindcss(?:\/[\w-]+)?["']?\s*\)\s*;?/g,
      '',
    );
    // tw-animate-css is a CSS-only package — esbuild marks it external so it ends up
    // as a bare @import in the style tag, which the browser can't resolve reliably
    // in srcdoc previews. Strip these imports to avoid MIME/404 noise.
    processedCss = processedCss.replace(/@import\s+["']tw-animate-css["'];?/g, '');
    processedCss = processedCss.replace(
      /@import\s+url\(\s*["']?tw-animate-css["']?\s*\)\s*;?/g,
      '',
    );

    // Strip @source directives — these tell the Tailwind CLI where to scan for class
    // names on disk, but @tailwindcss/browser@4 scans the DOM instead. Leaving them
    // causes the browser compiler to attempt relative fetches (e.g. "../components")
    // which fail in srcdoc iframes (origin is null) and can abort the entire compilation,
    // resulting in ZERO Tailwind utilities being generated.
    processedCss = processedCss.replace(/@source\s+["'][^"']*["']\s*;?/g, '');
    processedCss = processedCss.replace(/@source\s+[^\s;]+\s*;?/g, '');

    // Strip @plugin directives entirely — they reference npm packages (e.g.
    // "tailwindcss-animate") that may not be Tailwind v4-compatible or may fail
    // to load from esm.sh, which aborts the entire browser compilation and causes
    // ALL Tailwind utilities to silently disappear. The TAILWIND_DESIGN_BASELINE
    // already provides animation keyframes, and the browser script generates all
    // standard utilities without plugins.
    processedCss = processedCss.replace(/@plugin\s+["'][^"']*["']\s*;?/g, '');

    // Strip @config directives — they reference local tailwind.config.js files
    // which don't exist in the preview environment and abort browser compilation.
    processedCss = processedCss.replace(/@config\s+["'][^"']*["']\s*;?/g, '');
    processedCss = processedCss.replace(/@config\s+[^\s;]+\s*;?/g, '');
  }
  // Extract @import url("https://...") statements from CSS and hoist them to <link> tags
  // in <head>. CSS @import must be at the very top and only works in non-bundled CSS.
  // Extracting them prevents browser errors about @import position.
  const cssImportLinks: string[] = [];
  if (processedCss) {
    processedCss = processedCss.replace(
      /@import\s+url\(\s*(['"]?)(https?:\/\/[^'")]+)\1\s*\)\s*(?:layer\([^)]*\))?\s*;?/g,
      (_: string, _q: string, url: string) => {
        cssImportLinks.push(`<link rel="stylesheet" href="${url}">`);
        return '';
      },
    );
    // Also handle @import "https://..." (without url())
    processedCss = processedCss.replace(
      /@import\s+["'](https?:\/\/[^"']+)["']\s*;?/g,
      (_: string, url: string) => {
        cssImportLinks.push(`<link rel="stylesheet" href="${url}">`);
        return '';
      },
    );
  }
  // Escape </style patterns in CSS to prevent premature tag closing
  const safeCss = processedCss.replace(/<\/style/gi, '<\\/style');
  // Bundled CSS is plain standard CSS (Tailwind directives are stripped pre-esbuild).
  // Always use a regular <style> tag so CSS applies immediately without waiting for
  // @tailwindcss/browser@4 CDN. Tailwind directives (@theme, @custom-variant, etc.)
  // are in the separate TAILWIND_DESIGN_BASELINE <style type="text/tailwindcss"> block.
  const styleTag = safeCss.trim()
    ? `<style>
${safeCss}
</style>`
    : '';

  // Harden generated previews: prevent runtime crashes from missing lucide named exports
  // (e.g. `Sort`) by rewriting icon imports to resilient namespace lookups with fallbacks.
  let resilientBundledJs = rewriteLucideNamedImports(options.bundledJs);

  // Run a cheap sanitizer pass to harden common null/object operations (spreads, Object.assign)
  try {
    resilientBundledJs = sanitizeNullObjectOps(resilientBundledJs);
  } catch (err) {}

  // Run duplicate sanitizer only for non-React previews.
  // React bundles are highly sensitive to declaration rewrites here and this has
  // produced invalid blob modules ("Unexpected token 'var'").
  if (!needsReact) {
    try {
      resilientBundledJs = sanitizeDuplicatesAggressive(resilientBundledJs);
    } catch (err) {
      // Non-fatal — if sanitization fails, keep the original bundled JS
    }
  }

  // Drop unresolved virtual modules (e.g. "virtual:ascii-art") that can leak into
  // preview bundles and crash blob execution. Replace imports with safe stubs.
  try {
    resilientBundledJs = resilientBundledJs.replace(
      /^\s*import\s+(\*\s+as\s+)?([A-Za-z_$][\w$]*)\s+from\s+["']virtual:[^"']+["']\s*;?\s*$/gm,
      (_full: string, starPrefix: string, name: string) =>
        starPrefix ? `var ${name} = {};` : `var ${name} = undefined;`,
    );
    resilientBundledJs = resilientBundledJs.replace(
      /^\s*import\s*\{([^}]*)\}\s*from\s*["']virtual:[^"']+["']\s*;?\s*$/gm,
      (_full: string, names: string) => {
        const locals = names
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean)
          .map((part) => {
            const pieces = part.split(/\s+as\s+/);
            return (pieces[1] || pieces[0]).trim();
          })
          .filter(Boolean);
        return locals.map((local) => `var ${local} = undefined;`).join('\n');
      },
    );
    resilientBundledJs = resilientBundledJs.replace(
      /^\s*import\s+([A-Za-z_$][\w$]*)\s*,\s*\{([^}]*)\}\s*from\s*["']virtual:[^"']+["']\s*;?\s*$/gm,
      (_full: string, defaultLocal: string, names: string) => {
        const locals = names
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean)
          .map((part) => {
            const pieces = part.split(/\s+as\s+/);
            return (pieces[1] || pieces[0]).trim();
          })
          .filter(Boolean);
        return [
          `var ${defaultLocal} = undefined;`,
          ...locals.map((local) => `var ${local} = undefined;`),
        ].join('\n');
      },
    );
    resilientBundledJs = resilientBundledJs.replace(
      /^\s*import\s+["']virtual:[^"']+["']\s*;?\s*$/gm,
      '',
    );
    resilientBundledJs = resilientBundledJs.replace(
      /import\(\s*["']virtual:[^"']+["']\s*\)/g,
      'Promise.resolve({})',
    );
  } catch (err) {
    // Non-fatal
  }

  // IMPORTANT: do not do blanket const/let -> var conversion at build-time.
  // That broad rewrite has produced invalid syntax in some generated bundles
  // (e.g. blob module "Unexpected token 'var'"), which hard-crashes preview.
  // Runtime bootstrap already has targeted retry passes for TDZ/duplicate errors.

  // For React previews, avoid aggressive declaration rewrites at build-time.
  // Those rewrites can create malformed blobs ("Unexpected token 'var'") and
  // break hooks/import bindings. Runtime bootstrap handles safer retries.
  if (!needsReact) {
    // Convert function/class declarations to var assignments.
    // In ES modules, `export function/class` creates lexical bindings that
    // conflict with `var` declarations of the same name (e.g., from the
    // next-themes rewriter creating `var ThemeProvider = globalThis.__TASKING_NextThemes?.["ThemeProvider"]`).
    // Non-exported function/class at top-level are VarDeclaredNames and technically
    // don't conflict with `var`, but converting them too ensures the deduplicator
    // and import-conflict resolver work consistently.
    // Skips `export default function/class` which has different binding semantics.
    try {
      resilientBundledJs = resilientBundledJs.replace(
        /(^|[;\n}\)])\s*(export\s+default\s+|export\s+)?(async\s+)?function\s+([A-Za-z_$][\w$]*)\s*\(/gm,
        (full: string, prefix: string, exportKw: string, asyncKw: string, name: string) => {
          if (exportKw && /default/.test(exportKw)) return full;
          return `${prefix} ${exportKw || ''}var ${name} = ${asyncKw || ''}function ${name}(`;
        },
      );
      resilientBundledJs = resilientBundledJs.replace(
        /(^|[;\n}\)])\s*(export\s+default\s+|export\s+)?class\s+([A-Za-z_$][\w$]*)\s*([{\s]|extends)/gm,
        (full: string, prefix: string, exportKw: string, name: string, suffix: string) => {
          if (exportKw && /default/.test(exportKw)) return full;
          return `${prefix} ${exportKw || ''}var ${name} = class ${name} ${suffix}`;
        },
      );
    } catch (err) {
      // Non-fatal
    }

    // NUCLEAR: Resolve ALL import-vs-declaration conflicts.
    // In ES modules, import bindings (like const) can't coexist with var/function/class
    // declarations of the same name. Collect ALL identifiers declared anywhere by
    // var/function/class, then strip those from ALL import statements.
    try {
      const isReactLikeSpecifier = (fromSpec: string): boolean =>
        /["']react(?:\/jsx-runtime)?["']/.test(fromSpec) ||
        /["']react-dom(?:\/client)?["']/.test(fromSpec);

      // Step 1: Collect ALL var/function/class declared names (including inside blocks, etc.)
      const declaredNames = new Set<string>();
      resilientBundledJs.replace(
        /\b(?:var|function|class)\s+([A-Za-z_$][\w$]*)/g,
        (_full: string, name: string) => {
          declaredNames.add(name);
          return _full;
        },
      );

      if (declaredNames.size > 0) {
        // Step 2: Strip conflicting named bindings from import { ... } statements
        resilientBundledJs = resilientBundledJs.replace(
          /import\s+\{([^}]*)\}\s+from\s*(["'][^"']+["'])/g,
          (full: string, bindings: string, fromSpec: string) => {
            if (isReactLikeSpecifier(fromSpec)) return full;
            const parts = bindings
              .split(',')
              .map((b: string) => b.trim())
              .filter(Boolean);
            const kept: string[] = [];
            for (const part of parts) {
              // Extract the local binding name (handles "Foo as Bar" → "Bar")
              const m = part.match(/(?:\S+\s+as\s+)?(\S+)$/);
              if (m && declaredNames.has(m[1])) continue; // Conflict — strip
              kept.push(part);
            }
            if (kept.length === 0) return `/* import-conflict: removed from ${fromSpec} */`;
            if (kept.length === parts.length) return full; // No changes
            return `import { ${kept.join(', ')} } from ${fromSpec}`;
          },
        );

        // Step 3: Strip conflicting default imports: `import Foo from "..."` or `import Foo, { ... } from "..."`
        resilientBundledJs = resilientBundledJs.replace(
          /import\s+([A-Za-z_$][\w$]*)\s*(,\s*\{[^}]*\}\s+from\s*(["'][^"']+["']))/g,
          (full: string, defaultName: string, rest: string, fromSpec: string) => {
            if (isReactLikeSpecifier(fromSpec)) return full;
            if (declaredNames.has(defaultName)) {
              // Remove just the default binding, keep the named imports
              return `import ${rest.replace(/^,\s*/, '')}`;
            }
            return full;
          },
        );
        resilientBundledJs = resilientBundledJs.replace(
          /import\s+([A-Za-z_$][\w$]*)\s+from\s*(["'][^"']+["'])/g,
          (full: string, defaultName: string, fromSpec: string) => {
            if (isReactLikeSpecifier(fromSpec)) return full;
            if (declaredNames.has(defaultName)) {
              return `/* import-conflict: removed ${defaultName} from ${fromSpec} */`;
            }
            return full;
          },
        );

        // Step 4: Strip conflicting star imports: `import * as Foo from "..."`
        resilientBundledJs = resilientBundledJs.replace(
          /import\s+\*\s+as\s+([A-Za-z_$][\w$]*)\s+from\s*(["'][^"']+["'])/g,
          (full: string, starName: string, fromSpec: string) => {
            if (isReactLikeSpecifier(fromSpec)) return full;
            if (declaredNames.has(starName)) {
              return `/* import-conflict: removed * as ${starName} from ${fromSpec} */`;
            }
            return full;
          },
        );
      }
    } catch (err) {
      // Non-fatal
    }
  }

  // CRITICAL: Resolve bare specifiers in bundled JS to their actual URLs.
  // Blob URL modules CANNOT use the document's import map, so we must inline
  // the resolved URLs directly into the code. This includes both CDN URLs AND
  // data URL shims (styled-components, emotion, etc.) — blob modules need both.
  try {
    // Build a sorted-by-length-descending list so longer specifiers match first
    // (e.g. "react-dom/client" before "react-dom").
    // Use the FINAL importMap (with shims) so blob modules get the shim data URLs
    // for packages like styled-components that have custom lightweight implementations.
    const sortedEntries = Object.entries(importMap)
      .filter(([spec]) => !spec.endsWith('/'))
      .sort((a, b) => b[0].length - a[0].length);

    // Debug: check what bare specifiers exist in the bundled code
    const bareSpecsFound = sortedEntries
      .filter(
        ([spec]) =>
          resilientBundledJs.includes(`from "${spec}"`) ||
          resilientBundledJs.includes(`from '${spec}'`),
      )
      .map(([spec]) => spec);
    if (bareSpecsFound.length > 0) {
      previewVerboseLog(`[assembler] Bare specifiers found in bundled JS:`, bareSpecsFound);
    }

    // Specifiers that get rewritten to globalThis lookups (build-time + runtime bootstrap)
    // — don't inline their data/CDN URLs here since the globalThis rewriter handles them.
    // Only applies to React frameworks — non-React frameworks inline everything.
    const globalThisRewrittenSpecs = needsReact
      ? new Set([
          'react',
          'react/jsx-runtime',
          'react/jsx-dev-runtime',
          'react-dom/client',
          'react-router-dom',
          'next-themes',
          'next-themes/react',
          'lucide-react',
        ])
      : new Set<string>();
    for (const [spec, url] of sortedEntries) {
      if (globalThisRewrittenSpecs.has(spec)) continue;
      // Replace `from "specifier"` and `from 'specifier'`
      const escaped = spec.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&');
      const re = new RegExp(`(from\\s*)(["'])${escaped}\\2`, 'g');
      const before = resilientBundledJs;
      // Arrow-function replacement to prevent $ pattern corruption in URLs
      resilientBundledJs = resilientBundledJs.replace(
        re,
        (_full: string, prefix: string, quote: string) => `${prefix}${quote}${url}${quote}`,
      );
      // Also handle dynamic imports: import("specifier")
      const dynRe = new RegExp(`(import\\(\\s*)(["'])${escaped}\\2`, 'g');
      resilientBundledJs = resilientBundledJs.replace(
        dynRe,
        (_full: string, prefix: string, quote: string) => `${prefix}${quote}${url}${quote}`,
      );
      if (resilientBundledJs !== before) {
        previewVerboseLog(
          `[assembler] Inlined specifier "${spec}" → ${url.startsWith('data:') ? 'data:...' + url.slice(0, 60) : url}`,
        );
      }
    }
  } catch (err) {
    // Non-fatal — if resolution fails, bare specifiers will still work via import map
  }

  // BUILD-TIME: Rewrite React-specific imports to globalThis lookups.
  // Only for React/Next.js frameworks — non-React frameworks skip all React rewrites.
  if (needsReact) {
    // Rewrite react-router-dom imports to globalThis lookups.
    // This is the belt-and-suspenders companion to the runtime bootstrap rewriting.
    // esbuild renames imports (e.g. BrowserRouter → BrowserRouter2) to avoid conflicts.
    // Blob URL modules can't reliably import from CDN URLs (transitive bare specifiers fail).
    // So we rewrite ALL react-router-dom imports to use globalThis at build-time.
    try {
      const routerSpecifierRe = /react-router(-dom)?/;
      // Rewrite named imports: import { X as X2 } from "...react-router-dom..."
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\{([^}]*)\}\s*from\s*["']([^"']*)["']\s*;?/g,
        (full: string, names: string, specifier: string) => {
          if (!routerSpecifierRe.test(specifier)) return full;
          const parts = names
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
          const assignments = parts.map((name: string) => {
            const asParts = name.split(/\s+as\s+/);
            const imported = asParts[0].trim();
            const local = (asParts[1] || asParts[0]).trim();
            return `var ${local} = globalThis.__TASKING_ReactRouterDOM?.["${imported}"];`;
          });
          return assignments.join('\n');
        },
      );
      // Rewrite star imports: import * as X from "...react-router-dom..."
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\*\s*as\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*)["']\s*;?/g,
        (full: string, name: string, specifier: string) => {
          if (!routerSpecifierRe.test(specifier)) return full;
          return `var ${name} = globalThis.__TASKING_ReactRouterDOM || {};`;
        },
      );
      // Rewrite default imports: import X from "...react-router-dom..."
      resilientBundledJs = resilientBundledJs.replace(
        /import\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*)["']\s*;?/g,
        (full: string, name: string, specifier: string) => {
          if (!routerSpecifierRe.test(specifier)) return full;
          return `var ${name} = globalThis.__TASKING_ReactRouterDOM?.default || globalThis.__TASKING_ReactRouterDOM || {};`;
        },
      );
    } catch (err) {
      // Non-fatal
    }

    // BUILD-TIME: Rewrite react imports to globalThis lookups.
    // Prevents dual-React-instances (useState is not a function) in blob URL modules.
    // The react specifier regex matches "react" or CDN URLs containing /react@ but
    // NOT react-dom, react-router, or react/jsx-runtime (those have their own rewrites).
    try {
      const reactSpecifierRe = /(?:^|[/"'])react(?:@[^/"'\\]*)?\s*$/;
      // Mixed imports: import React, { useState } from "react"
      resilientBundledJs = resilientBundledJs.replace(
        /import\s+([A-Za-z_$][\w$]*)\s*,\s*\{([^}]*)\}\s*from\s*["']([^"']*?)["']\s*;?/g,
        (full: string, defaultName: string, names: string, specifier: string) => {
          if (!reactSpecifierRe.test(specifier)) return full;
          const defaultAssign = `var ${defaultName} = (function(){ var R = globalThis.__TASKING_React || globalThis.__TASKING_React?.default || {}; if (typeof R.useState !== "function" && globalThis.__TASKING_getReactExport) { R = Object.assign({}, R, { useState: globalThis.__TASKING_getReactExport("useState"), useEffect: globalThis.__TASKING_getReactExport("useEffect"), useLayoutEffect: globalThis.__TASKING_getReactExport("useLayoutEffect"), useMemo: globalThis.__TASKING_getReactExport("useMemo"), useCallback: globalThis.__TASKING_getReactExport("useCallback"), useRef: globalThis.__TASKING_getReactExport("useRef"), createElement: globalThis.__TASKING_getReactExport("createElement"), createContext: globalThis.__TASKING_getReactExport("createContext"), useContext: globalThis.__TASKING_getReactExport("useContext") }); } return R; })();`;
          const parts = names
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
          const namedAssignments = parts.map((name: string) => {
            const asParts = name.split(/\s+as\s+/);
            const imported = asParts[0].trim();
            const local = (asParts[1] || asParts[0]).trim();
            if (imported === 'useState') {
              return `var ${local} = (function(){ var _u = (globalThis.__TASKING_getReactExport ? globalThis.__TASKING_getReactExport("useState") : (globalThis.__TASKING_React?.["useState"] ?? globalThis.__TASKING_React?.default?.["useState"])); if (typeof _u !== "function") _u = function(initial){ return [typeof initial === "function" ? initial() : initial, function(){}]; }; return function(initial){ try { var _r = _u.apply(this, arguments); if (Array.isArray(_r) || (_r && typeof _r[Symbol.iterator] === "function")) return _r; } catch (e) {} return [typeof initial === "function" ? initial() : initial, function(){}]; }; })();`;
            }
            return `var ${local} = (globalThis.__TASKING_getReactExport ? globalThis.__TASKING_getReactExport("${imported}") : (globalThis.__TASKING_React?.["${imported}"] ?? globalThis.__TASKING_React?.default?.["${imported}"]));`;
          });
          return [defaultAssign, ...namedAssignments].join('\n');
        },
      );
      // Named imports: import { useState, useEffect } from "react"
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\{([^}]*)\}\s*from\s*["']([^"']*?)["']\s*;?/g,
        (full: string, names: string, specifier: string) => {
          if (!reactSpecifierRe.test(specifier)) return full;
          const parts = names
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
          const assignments = parts.map((name: string) => {
            const asParts = name.split(/\s+as\s+/);
            const imported = asParts[0].trim();
            const local = (asParts[1] || asParts[0]).trim();
            if (imported === 'useState') {
              return `var ${local} = (function(){ var _u = (globalThis.__TASKING_getReactExport ? globalThis.__TASKING_getReactExport("useState") : (globalThis.__TASKING_React?.["useState"] ?? globalThis.__TASKING_React?.default?.["useState"])); if (typeof _u !== "function") _u = function(initial){ return [typeof initial === "function" ? initial() : initial, function(){}]; }; return function(initial){ try { var _r = _u.apply(this, arguments); if (Array.isArray(_r) || (_r && typeof _r[Symbol.iterator] === "function")) return _r; } catch (e) {} return [typeof initial === "function" ? initial() : initial, function(){}]; }; })();`;
            }
            return `var ${local} = (globalThis.__TASKING_getReactExport ? globalThis.__TASKING_getReactExport("${imported}") : (globalThis.__TASKING_React?.["${imported}"] ?? globalThis.__TASKING_React?.default?.["${imported}"]));`;
          });
          return assignments.join('\n');
        },
      );
      // Star imports: import * as React from "react"
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\*\s*as\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*?)["']\s*;?/g,
        (full: string, name: string, specifier: string) => {
          if (!reactSpecifierRe.test(specifier)) return full;
          return `var ${name} = (function(){ var R = globalThis.__TASKING_React || globalThis.__TASKING_React?.default || {}; if (typeof R.useState !== "function" && globalThis.__TASKING_getReactExport) { R = Object.assign({}, R, { useState: globalThis.__TASKING_getReactExport("useState"), useEffect: globalThis.__TASKING_getReactExport("useEffect"), useLayoutEffect: globalThis.__TASKING_getReactExport("useLayoutEffect"), useMemo: globalThis.__TASKING_getReactExport("useMemo"), useCallback: globalThis.__TASKING_getReactExport("useCallback"), useRef: globalThis.__TASKING_getReactExport("useRef"), createElement: globalThis.__TASKING_getReactExport("createElement"), createContext: globalThis.__TASKING_getReactExport("createContext"), useContext: globalThis.__TASKING_getReactExport("useContext") }); } return R; })();`;
        },
      );
      // Default imports: import React from "react"
      resilientBundledJs = resilientBundledJs.replace(
        /import\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*?)["']\s*;?/g,
        (full: string, name: string, specifier: string) => {
          if (!reactSpecifierRe.test(specifier)) return full;
          return `var ${name} = (function(){ var R = globalThis.__TASKING_React || globalThis.__TASKING_React?.default || {}; if (typeof R.useState !== "function" && globalThis.__TASKING_getReactExport) { R = Object.assign({}, R, { useState: globalThis.__TASKING_getReactExport("useState"), useEffect: globalThis.__TASKING_getReactExport("useEffect"), useLayoutEffect: globalThis.__TASKING_getReactExport("useLayoutEffect"), useMemo: globalThis.__TASKING_getReactExport("useMemo"), useCallback: globalThis.__TASKING_getReactExport("useCallback"), useRef: globalThis.__TASKING_getReactExport("useRef"), createElement: globalThis.__TASKING_getReactExport("createElement"), createContext: globalThis.__TASKING_getReactExport("createContext"), useContext: globalThis.__TASKING_getReactExport("useContext") }); } return R; })();`;
        },
      );
    } catch (err) {
      // Non-fatal
    }

    // BUILD-TIME: Rewrite react/jsx-runtime imports to globalThis lookups.
    // esbuild automatic JSX transform outputs `from "react/jsx-runtime"` which blob
    // modules can't resolve (no import map access, trailing-slash entries filtered).
    try {
      const jsxRtSpecifierRe = /react(?:@[^/"'\\]*)?\/jsx(?:-dev)?-runtime/;
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\{([^}]*)\}\s*from\s*["']([^"']*?)["']\s*;?/g,
        (full: string, names: string, specifier: string) => {
          if (!jsxRtSpecifierRe.test(specifier)) return full;
          const parts = names
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
          const assignments = parts.map((name: string) => {
            const asParts = name.split(/\s+as\s+/);
            const imported = asParts[0].trim();
            const local = (asParts[1] || asParts[0]).trim();
            return `var ${local} = globalThis.__TASKING_JsxRuntime?.["${imported}"] || globalThis.__TASKING_React?.["${imported}"];`;
          });
          return assignments.join('\n');
        },
      );
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\*\s*as\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*?)["']\s*;?/g,
        (full: string, name: string, specifier: string) => {
          if (!jsxRtSpecifierRe.test(specifier)) return full;
          return `var ${name} = globalThis.__TASKING_JsxRuntime || {};`;
        },
      );
    } catch (err) {
      // Non-fatal
    }

    // BUILD-TIME: Catch-all rewrite for any remaining lucide-react imports that
    // rewriteLucideNamedImports missed (e.g., CDN-inlined URLs, deep imports).
    // Same pattern as the router rewrite — convert to globalThis lookups.
    try {
      const lucideSpecifierRe = /lucide-react/;
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\{([^}]*)\}\s*from\s*["']([^"']*)["']\s*;?/g,
        (full: string, names: string, specifier: string) => {
          if (!lucideSpecifierRe.test(specifier)) return full;
          const parts = names
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
          const assignments = parts.map((name: string) => {
            const asParts = name.split(/\s+as\s+/);
            const imported = asParts[0].trim();
            const local = (asParts[1] || asParts[0]).trim();
            return `var ${local} = globalThis["${imported}"] = (globalThis.__TASKING_Lucide || {})["${imported}"] || (() => null);`;
          });
          return assignments.join('\n');
        },
      );
      // Star imports: import * as Icons from "lucide-react"
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\*\s*as\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*)["']\s*;?/g,
        (full: string, name: string, specifier: string) => {
          if (!lucideSpecifierRe.test(specifier)) return full;
          return `var ${name} = globalThis.__TASKING_Lucide || {};`;
        },
      );
    } catch (err) {
      // Non-fatal
    }

    // BUILD-TIME: Rewrite `export { X } from "lucide-react"` re-exports.
    // esbuild may produce re-exports instead of imports for certain code patterns.
    // Blob URL modules can't resolve bare/CDN specifiers in export-from statements.
    // Convert to: const X = (globalThis.__TASKING_Lucide || {})["X"] || (() => null); export { X };
    try {
      resilientBundledJs = resilientBundledJs.replace(
        /export\s*\{([^}]*)\}\s*from\s*["']([^"']*)["']\s*;?/g,
        (full: string, names: string, specifier: string) => {
          if (!/lucide-react/.test(specifier)) return full;
          const parts = names
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
          const decls: string[] = [];
          const exportNames: string[] = [];
          for (const part of parts) {
            const asParts = part.split(/\s+as\s+/);
            const imported = asParts[0].trim();
            const exported = (asParts[1] || asParts[0]).trim();
            decls.push(
              `var ${exported} = globalThis["${imported}"] = (globalThis.__TASKING_Lucide || {})["${imported}"] || (() => null);`,
            );
            exportNames.push(exported);
          }
          return decls.join('\n') + '\nexport { ' + exportNames.join(', ') + ' };';
        },
      );
    } catch (err) {
      // Non-fatal
    }

    // BUILD-TIME: Rewrite next-themes imports to globalThis lookups.
    // AI-generated code frequently uses <ThemeProvider> / useTheme() from next-themes.
    // The data URL shim can fail to import from blob URL modules in srcdoc iframes,
    // causing "ThemeProvider is not defined". Rewriting to globalThis lookups ensures
    // ThemeProvider is always resolved from the pre-loaded __TASKING_NextThemes.
    try {
      // Match "next-themes", "next-themes/react", or CDN URLs containing next-themes
      const nextThemesSpecifierRe = /next-themes/;
      // Named imports: import { ThemeProvider, useTheme } from "next-themes"
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\{([^}]*)\}\s*from\s*["']([^"']*)["']\s*;?/g,
        (full: string, names: string, specifier: string) => {
          if (!nextThemesSpecifierRe.test(specifier)) return full;
          const parts = names
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
          const assignments = parts.map((name: string) => {
            const asParts = name.split(/\s+as\s+/);
            const imported = asParts[0].trim();
            const local = (asParts[1] || asParts[0]).trim();
            return `var ${local} = globalThis.__TASKING_NextThemes?.["${imported}"];`;
          });
          return assignments.join('\n');
        },
      );
      // Star imports: import * as NextThemes from "next-themes"
      resilientBundledJs = resilientBundledJs.replace(
        /import\s*\*\s*as\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*)["']\s*;?/g,
        (full: string, name: string, specifier: string) => {
          if (!nextThemesSpecifierRe.test(specifier)) return full;
          return `var ${name} = globalThis.__TASKING_NextThemes || {};`;
        },
      );
      // Default imports: import NextThemes from "next-themes"
      resilientBundledJs = resilientBundledJs.replace(
        /import\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*)["']\s*;?/g,
        (full: string, name: string, specifier: string) => {
          if (!nextThemesSpecifierRe.test(specifier)) return full;
          return `var ${name} = globalThis.__TASKING_NextThemes?.default || globalThis.__TASKING_NextThemes || {};`;
        },
      );
    } catch (err) {
      // Non-fatal
    }
  } // end needsReact build-time rewrites

  // Inject bundled module via dynamic import wrapper so parse-time SyntaxErrors
  // (e.g. duplicate top-level declarations) can be caught and auto-sanitized at runtime.
  // Escape all </script patterns (including </script >, </script/>) to prevent
  // the HTML parser from prematurely closing the script tag.
  // Also strip source maps to reduce size and avoid potential parsing issues.
  let safeBundledJs = resilientBundledJs
    .replace(/<\/script/gi, '<\\/script')
    // Strip source map comments (including multiline data URLs) - match everything after sourceMappingURL= to end of string
    // Strip source maps - match everything from sourceMappingURL= to end of string
    .replace(/\/\/#\s*sourceMappingURL=.*$/m, '');
  // Use JSON.stringify to safely embed code as a string literal.
  // This handles all escaping (backslashes, quotes, newlines, etc.) correctly.
  // Also escape </script and <!-- to prevent HTML parser interference.
  const jsonEscapedCode = JSON.stringify(safeBundledJs)
    .replace(/<\//g, '<\\/')
    .replace(/<!--/g, '\\u003C!--');

  // Define regex patterns as raw strings, then embed via JSON.stringify() in the template literal.
  // This avoids double-escaping hell: template literal `\'` evaluates to `'`, which breaks
  // single-quoted JS strings in the generated srcdoc. JSON.stringify handles all escaping correctly.
  const RE_RDC = String.raw`react-dom(?:@[^/"'\\]*)?[/]client`;
  // Match react-router-dom (bare specifier or CDN URL like https://esm.sh/react-router-dom@...)
  const RE_ROUTER = String.raw`react-router-dom`;
  // Match "react" bare specifier or CDN URL (but NOT react-dom, react-router, react/jsx-runtime)
  const RE_REACT = String.raw`(?:^|[/"'])react(?:@[^/"'\\]*)?\s*$`;
  // Match react/jsx-runtime or react/jsx-dev-runtime (bare or CDN)
  const RE_JSX_RUNTIME = String.raw`react(?:@[^/"'\\]*)?/jsx(?:-dev)?-runtime`;
  // Match next-themes or next-themes/react (bare specifier or CDN URL)
  const RE_NEXT_THEMES = String.raw`next-themes(?:\/react)?`;
  const RE_DEFAULT_NAMED_IMPORT = String.raw`import\s+([A-Za-z_$][\w$]*)\s*,\s*\{([^}]*)\}\s*from\s*["']([^"']*)["']\s*;?`;
  const RE_NAMED_IMPORT = String.raw`import\s*\{([^}]*)\}\s*from\s*["']([^"']*)["']\s*;?`;
  const RE_STAR_IMPORT = String.raw`import\s*\*\s*as\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*)["']\s*;?`;
  const RE_DEFAULT_IMPORT = String.raw`import\s+([A-Za-z_$][\w$]*)\s+from\s*["']([^"']*)["']\s*;?`;

  // Build the module script. For React/Next.js, use the full bootstrap that pre-loads
  // React, rewrites imports, etc. For other frameworks, use a simple inline script.
  let scriptTag: string;
  if (needsReact) {
    // Full React bootstrap with import map pre-loading and bare specifier rewriting
    scriptTag = moduleBootstrapRaw
      .replace('"__BUNDLED_CODE__"', () => jsonEscapedCode)
      .replaceAll('"__RE_RDC__"', () => JSON.stringify(RE_RDC))
      .replaceAll('"__RE_ROUTER__"', () => JSON.stringify(RE_ROUTER))
      .replaceAll('"__RE_REACT__"', () => JSON.stringify(RE_REACT))
      .replaceAll('"__RE_JSX_RUNTIME__"', () => JSON.stringify(RE_JSX_RUNTIME))
      .replaceAll('"__RE_NEXT_THEMES__"', () => JSON.stringify(RE_NEXT_THEMES))
      .replaceAll('"__RE_DEFAULT_NAMED_IMPORT__"', () => JSON.stringify(RE_DEFAULT_NAMED_IMPORT))
      .replaceAll('"__RE_NAMED_IMPORT__"', () => JSON.stringify(RE_NAMED_IMPORT))
      .replaceAll('"__RE_STAR_IMPORT__"', () => JSON.stringify(RE_STAR_IMPORT))
      .replaceAll('"__RE_DEFAULT_IMPORT__"', () => JSON.stringify(RE_DEFAULT_IMPORT));
  } else {
    // Lightweight bootstrap for non-React frameworks (vanilla, vue, svelte, angular).
    // No React pre-loading, no React import rewriting — just the user's bundled code.
    // NOTE: Blob URL modules cannot use the document's import map for THEIR OWN imports,
    // but that's OK because the URL inlining step (above) already replaced all bare
    // specifiers with absolute CDN URLs. The HTML import map is still needed for
    // TRANSITIVE resolution — when CDN modules import other packages as bare specifiers
    // (e.g. vue-router importing "vue"), the browser uses the document's import map.
    scriptTag = `
// Non-React framework — direct module execution
try {
  const __code = ${jsonEscapedCode};
  // All bare specifiers were inlined to absolute CDN URLs by the assembler.
  // Blob URL execution is safe — no bare specifiers remain in the bundled code.
  const __blob = new Blob([__code], { type: 'text/javascript' });
  const __url = URL.createObjectURL(__blob);
  try { await import(__url); } finally { URL.revokeObjectURL(__url); }
} catch(err) {
  console.error('[preview] Module execution error:', err);
  if (typeof document !== 'undefined') {
    const errDiv = document.createElement('div');
    errDiv.style.cssText = 'position:fixed;top:0;left:0;right:0;padding:16px;background:#1a1a2e;color:#a0aec0;font-family:system-ui,sans-serif;font-size:14px;z-index:99999;text-align:center;border-bottom:2px solid #4a5568';
    errDiv.innerHTML = '<div style="font-size:24px;margin-bottom:8px">\\u26A0\\uFE0F</div><div>Something went wrong loading the preview</div><div style="font-size:12px;opacity:0.6;margin-top:4px">The team is auto-fixing — try refreshing</div>';
    document.body?.appendChild(errDiv);
  }
}
`;
  }

  // Tailwind v4 browser CDN (processes utility classes + @apply in browser)
  // Design baseline is injected in both modes; tw-animate imports are stripped above.
  // Non-Tailwind projects still get FONT_BASELINE (Google Fonts + CSS reset).
  // The onerror handler logs CDN failures — the base utility fallback CSS in the
  // TAILWIND_DESIGN_BASELINE ensures the UI is still usable when the CDN fails.
  const tailwindTag = options.hasTailwind
    ? `<script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4" onerror="console.warn('[preview] @tailwindcss/browser@4 CDN failed to load — using fallback CSS');window.__twFailed=true;"></script>\n${TAILWIND_DESIGN_BASELINE}
<script>
/* Tailwind CSS fallback: if @tailwindcss/browser@4 fails to load or doesn't compile
   within 3s, promote type="text/tailwindcss" styles to regular <style> so user CSS
   isn't silently lost. Strips @theme/@apply/@layer directives that only Tailwind understands. */
setTimeout(function(){
  if(window.__twFailed||!document.querySelector('style[data-tailwind]')){
    document.querySelectorAll('style[type="text/tailwindcss"]').forEach(function(s){
      var css=s.textContent||'';
      /* Strip Tailwind-only directives */
      css=css.replace(/@theme\\s+inline\\s*\\{[^}]*\\}/gs,'');
      css=css.replace(/@theme\\s*\\{[^}]*\\}/gs,'');
      css=css.replace(/@apply\\s+[^;]+;/g,'');
      css=css.replace(/@layer\\s+\\w+\\s*\\{/g,'{');
      css=css.replace(/@custom-variant[^;]+;/g,'');
      css=css.replace(/@source[^;]+;/g,'');
      css=css.replace(/@plugin[^;]+;/g,'');
      css=css.replace(/@import\\s+["']tailwindcss[^"']*["'][^;]*;?/g,'');
      if(css.trim()){
        var ns=document.createElement('style');
        ns.textContent=css;
        ns.setAttribute('data-tw-fallback','true');
        s.parentNode.insertBefore(ns,s.nextSibling);
        console.debug('[preview] Tailwind fallback: promoted user CSS to regular style');
      }
    });
  }
},1200);
</script>`
    : FONT_BASELINE;

  // Angular requires zone.js loaded before any Angular code
  const zoneJsTag = options.hasAngular ? '<script src="https://esm.sh/zone.js"></script>' : '';

  // Inject into <head> as early as possible so import map is parsed before any scripts.
  const cssLinkTags = cssImportLinks.join('\n');
  const headOpenMatch = html.match(/<head[^>]*>/i);
  if (headOpenMatch) {
    const headOpen = headOpenMatch[0];
    html = html.replace(
      headOpen,
      `${headOpen}
${importMapTag}
${zoneJsTag}
${tailwindTag}
${styleTag}
${cssLinkTags}`,
    );
  } else {
    // No <head> — prepend to content
    html = `${importMapTag}
${zoneJsTag}
${tailwindTag}
${styleTag}
${cssLinkTags}
${html}`;
  }

  // Inject before </body>
  // Wrap bootstrap in <script type="module"> since we removed the tags from the file
  // Suppress TDZ errors silently (e.g. "Cannot access 'X' before initialization")
  // We override console.error first to filter out TDZ errors before they get logged
  const wrappedBootstrap = `<script type="module">
(function() {
  // Override console.error to suppress TDZ errors before they get logged
  var __origConsoleError = console.error;
  console.error = function() {
    var msg = arguments[0] && arguments[0].message ? arguments[0].message : String(arguments[0] || '');
    if (msg.includes('Cannot access') && msg.includes('before initialization')) {
      return; // Suppress TDZ errors silently
    }
    return __origConsoleError.apply(console, arguments);
  };
  // Also suppress uncaught errors by patching the error event
  var __origOnerror = window.onerror;
  window.onerror = function(msg, src, line, col, err) {
    if (msg && msg.toString().includes('Cannot access') && msg.toString().includes('before initialization')) {
      return true; // Suppress TDZ errors
    }
    if (__origOnerror) return __origOnerror(msg, src, line, col, err);
    return false;
  };
})();
try {
${scriptTag
  .split('\n')
  .map((line) => '  ' + line)
  .join('\n')}
} catch(err) {
  // Silently suppress TDZ initialization errors - they don't affect functionality
  if (!(err?.message?.includes('Cannot access') && err?.message?.includes('before initialization'))) {
    throw err;
  }
}
</script>`;

  if (html.includes('</body>')) {
    html = html.replace(
      '</body>',
      `${PREVIEW_NAV_GUARD_SCRIPT}
${PREVIEW_INSPECTOR_SCRIPT}
${wrappedBootstrap}
</body>`,
    );
  } else {
    html += `
${PREVIEW_NAV_GUARD_SCRIPT}
${PREVIEW_INSPECTOR_SCRIPT}
${wrappedBootstrap}`;
  }

  // Final safety pass: regardless of upstream template state, keep exactly one import map.
  html = canonicalizeImportMapsInHtml(html);

  // Sanity check: only one <script type="module"> should remain (our bundled script).
  // If you see "react-dom/client" resolve errors, inspect srcdoc in devtools and ensure
  // there are no extra module scripts and that the import map includes "react-dom/client".
  const moduleScriptMatches = html.match(/<script[^>]*\btype\s*=\s*["']module["'][^>]*>/gi) || [];
  const moduleScriptCount = moduleScriptMatches.length;
  const isDev =
    (typeof import.meta !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV) ||
    (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development');
  if (moduleScriptCount !== 1 && isDev) {
    console.warn(
      '[assembleHtml] Expected exactly one <script type="module"> in preview HTML, got',
      moduleScriptCount,
      '— check that inline module scripts were stripped and only bundledJs was injected.',
    );
  }

  return html;
}
